import React, { useEffect, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Lock, LogOut, Save, RotateCcw, Package, ShoppingBag, AlertCircle, Settings as SettingsIcon, Edit3, Upload, Send, Eye, X, Check, Star, Truck, Trash2, RotateCw, Plus, Mail, Printer, Download, Activity, RefreshCw, Boxes, Search, Info, Clock, Wallet, ExternalLink, ShieldAlert, ArrowUpRight, MailWarning, Stethoscope, ChevronDown } from "lucide-react";
import { api,
    adminLogin, adminClearOverride, adminListOrders,
    adminListProductsFull, adminPatchProductFull, adminUploadImage,
    adminGetSettings, adminPatchSettings, adminSendTracking,
    adminDeleteProduct, adminRestoreProduct, adminCreateProduct, adminTestEmail,
    adminCreateLabel, adminSystemStatus, adminShippoSmokeTest, adminClearMoleculeOverride,
    adminListDiscountCodes, adminUpsertDiscountCode, adminDeleteDiscountCode,
    adminDeleteOrder, adminClearAllOrders, adminVerifyPayment,
    adminMarkPaid, adminMarkDelivered, adminMarkCompleted,
    adminSendRecoveryEmail, adminOrderDiagnostics,
} from "@/lib/api";
import { RestrictedAdminPanel } from "@/components/RestrictedAdminPanel";
import { toast } from "sonner";

const TOKEN_KEY = "helixora_admin_token";

function LoginForm({ onLogin }) {
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);
    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const r = await adminLogin(password);
            if (r.token) { localStorage.setItem(TOKEN_KEY, r.token); onLogin(r.token); }
        } catch { toast.error("Invalid password"); setLoading(false); }
    };
    return (
        <div className="min-h-screen bg-ink-base flex items-center justify-center px-6">
            <form onSubmit={submit} className="w-full max-w-sm border border-ink-strong bg-ink-surface p-8" data-testid="admin-login">
                <Lock className="w-6 h-6 text-neon" strokeWidth={1.5} />
                <h1 className="mt-4 font-display text-3xl font-bold text-ink">Helixora Admin</h1>
                <p className="mt-2 text-sm text-ink-dim">Restricted access — store management.</p>
                <label className="block mt-6">
                    <span className="overline text-[10px] text-ink-mute mb-2 block">PASSWORD</span>
                    <input type="password" autoFocus value={password} onChange={(e) => setPassword(e.target.value)}
                        className="w-full border border-ink-strong bg-ink-base text-ink px-4 py-3 text-sm focus:outline-none focus:border-neon"
                        data-testid="admin-password" />
                </label>
                <button type="submit" disabled={loading}
                    className="w-full mt-6 bg-neon hover:bg-neon-glow text-black px-6 py-3 font-bold tracking-wide disabled:opacity-60"
                    data-testid="admin-login-submit">
                    {loading ? "CHECKING…" : "SIGN IN"}
                </button>
            </form>
        </div>
    );
}

/* ============ PRODUCT EDITOR ============ */
function ProductMoleculeEditor({ draft, set, currentMolecule, defaultMolecule, onUseDefault }) {
    // draft.molecule is EITHER undefined (= use site default), OR { clear: true }, OR a real molecule object
    const active = draft.molecule !== undefined ? draft.molecule : currentMolecule;
    const source = (active && !active.clear) ? (active.source || "") : "";
    const id = (active && !active.clear) ? (active.id || "") : "";
    const label = (active && !active.clear) ? (active.label || "") : "";
    const isCleared = !!(draft.molecule && draft.molecule.clear);
    const hasOverride = draft.molecule !== undefined && !isCleared;
    const usingDefault = !hasOverride && !isCleared && !!defaultMolecule;

    const update = (field, val) => {
        const next = {
            source: field === "source" ? val : source,
            id: field === "id" ? val : id,
            label: field === "label" ? val : label,
        };
        if (!next.source && !next.id && !next.label) {
            set("molecule", undefined); // revert to site default
        } else {
            set("molecule", next);
        }
    };
    const useDefault = () => { if (onUseDefault) onUseDefault(); else set("molecule", undefined); };
    const clearModel = () => set("molecule", { clear: true });

    return (
        <div className="border border-neon/30 p-5 bg-neon/5 space-y-4" data-testid="edit-molecule-block">
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <p className="overline text-neon text-[10px]">3D MOLECULE MODEL <span className="text-ink-mute">· rendered on product page</span></p>
                    <p className="text-xs text-ink-dim mt-1">PubChem CID renders small peptides. PDB ID renders proteins. The 3D viewer on the product page reads from here.</p>
                </div>
                <div className="flex gap-2">
                    {hasOverride && (
                        <button type="button" onClick={useDefault} className="px-3 py-1.5 text-[10px] font-mono uppercase tracking-wider border border-ink-strong hover:border-neon text-ink-dim hover:text-neon" data-testid="mol-use-default">
                            ↻ USE DEFAULT
                        </button>
                    )}
                    {!isCleared && (
                        <button type="button" onClick={clearModel} className="px-3 py-1.5 text-[10px] font-mono uppercase tracking-wider border border-ink-strong hover:border-red-400 text-ink-dim hover:text-red-400" data-testid="mol-clear">
                            × NO MODEL
                        </button>
                    )}
                </div>
            </div>

            <div className="text-[10px] font-mono uppercase tracking-wider">
                {isCleared && <span className="text-red-400">● EXPLICITLY CLEARED — no 3D model on product page</span>}
                {usingDefault && <span className="text-neon">● USING SITE DEFAULT — {defaultMolecule.dual ? `DUAL: ${defaultMolecule.left?.source?.toUpperCase()} ${defaultMolecule.left?.id} + ${defaultMolecule.right?.source?.toUpperCase()} ${defaultMolecule.right?.id}` : `${(defaultMolecule.source || "").toUpperCase()} ${defaultMolecule.id}`}</span>}
                {hasOverride && <span className="text-gold">● CUSTOM OVERRIDE ACTIVE</span>}
                {!usingDefault && !hasOverride && !isCleared && <span className="text-ink-mute">● NO DEFAULT AVAILABLE — add one below</span>}
            </div>

            {active && active.dual && usingDefault && (
                <div className="p-3 border border-ink-strong bg-ink-base text-xs space-y-1 font-mono">
                    <div className="text-neon">LEFT · {active.left?.source?.toUpperCase()} {active.left?.id}</div>
                    <div className="text-ink-dim">{active.left?.label}</div>
                    <div className="text-neon mt-2">RIGHT · {active.right?.source?.toUpperCase()} {active.right?.id}</div>
                    <div className="text-ink-dim">{active.right?.label}</div>
                    <p className="text-[10px] text-ink-mute mt-2">This is a dual-molecule blend. Editing below will replace it with a single-molecule override.</p>
                </div>
            )}

            <div className="grid md:grid-cols-3 gap-3">
                <Field label="Source">
                    <select className="inp" value={source} onChange={(e) => update("source", e.target.value)} data-testid="edit-mol-source">
                        <option value="">— none —</option>
                        <option value="pubchem">PubChem (small peptide)</option>
                        <option value="pdb">PDB (protein structure)</option>
                    </select>
                </Field>
                <Field label="ID">
                    <input className="inp font-mono" placeholder={source === "pdb" ? "e.g. 1HGU" : "e.g. 9941957"}
                        value={id} onChange={(e) => update("id", e.target.value)} data-testid="edit-mol-id" />
                </Field>
                <Field label="Label (optional)">
                    <input className="inp" placeholder="e.g. 15-aa pentadecapeptide" value={label} onChange={(e) => update("label", e.target.value)} data-testid="edit-mol-label" />
                </Field>
            </div>
            <p className="text-[10px] text-ink-mute font-mono">
                PUBCHEM CID → <a href="https://pubchem.ncbi.nlm.nih.gov/" target="_blank" rel="noopener" className="text-neon underline">pubchem.ncbi.nlm.nih.gov</a> · PDB ID → <a href="https://www.rcsb.org/" target="_blank" rel="noopener" className="text-neon underline">rcsb.org</a>
            </p>
        </div>
    );
}

/* ============ DOSE-CALCULATOR EDITOR ============
   Lets admin set IU/mg per vial + kit size without touching JSON.
   Stored as draft.dose_calc; persisted via AdminProductPatch.dose_calc. */
function DoseCalcEditor({ draft, set }) {
    const dc = draft.dose_calc || {};
    const update = (k, v) => set("dose_calc", { ...dc, [k]: v });
    const numOrNull = (s) => (s === "" ? null : parseFloat(s));
    const unitOptions = ["mg", "mcg", "IU"];

    const totalLabel = (() => {
        const va = parseFloat(dc.vial_amount);
        const ks = parseInt(dc.kit_size, 10) || 1;
        if (!va || va <= 0) return null;
        const total = va * ks;
        return `${ks > 1 ? `${ks} × ` : ""}${va}${dc.unit === "IU" ? " IU" : (dc.unit || "")} = ${total}${dc.unit === "IU" ? " IU" : (dc.unit || "")} total`;
    })();

    return (
        <div className="p-4 border border-neon/30 bg-neon/5" data-testid="dose-calc-editor">
            <div className="mb-3">
                <p className="overline text-neon text-[10px]">DOSE CALCULATOR · IU / MG PER VIAL</p>
                <p className="text-[11px] text-ink-mute mt-1">Controls the "How long will it last?" widget on the product page. For bulk kits, set BOTH vial amount AND kit size — the calculator multiplies them. To revert to defaults, use "Reset all overrides" at the bottom.</p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Field label="Vial amount">
                    <input type="number" step="0.1" min="0" className="inp font-mono"
                        value={dc.vial_amount ?? ""}
                        onChange={(e) => update("vial_amount", numOrNull(e.target.value))}
                        placeholder="e.g. 36"
                        data-testid="dose-calc-vial-amount" />
                </Field>
                <Field label="Unit">
                    <select className="inp font-mono"
                        value={dc.unit || "mg"}
                        onChange={(e) => update("unit", e.target.value)}
                        data-testid="dose-calc-unit">
                        {unitOptions.map((u) => <option key={u} value={u}>{u}</option>)}
                    </select>
                </Field>
                <Field label="Kit size (vials per kit · use 1 for single)">
                    <input type="number" step="1" min="1" className="inp font-mono"
                        value={dc.kit_size ?? 1}
                        onChange={(e) => update("kit_size", parseInt(e.target.value, 10) || 1)}
                        data-testid="dose-calc-kit-size" />
                </Field>
                <Field label="Default research dose (in chosen unit)">
                    <input type="number" step="0.1" min="0" className="inp font-mono"
                        value={dc.default_dose ?? ""}
                        onChange={(e) => update("default_dose", numOrNull(e.target.value))}
                        placeholder="e.g. 2"
                        data-testid="dose-calc-default-dose" />
                </Field>
            </div>
            {totalLabel && (
                <p className="mt-3 text-[11px] font-mono text-neon">▶ {totalLabel}</p>
            )}
        </div>
    );
}


function ProductEditorModal({ product, token, onClose, onSaved }) {
    const [draft, setDraft] = useState({ ...product.current });
    const [busy, setBusy] = useState(false);
    const fileRef = useRef(null);
    const coaFileRef = useRef(null);
    const frontImgRef = useRef(null);

    const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

    const handleUpload = async (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        setBusy(true);
        try {
            const { url } = await adminUploadImage(token, f);
            set("image", url);
            toast.success("Image uploaded");
        } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
        setBusy(false);
    };

    const handleFrontImageUpload = async (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        setBusy(true);
        try {
            const { url } = await adminUploadImage(token, f);
            set("front_image", url);
            toast.success("Front photo uploaded — saves on click");
        } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
        setBusy(false);
    };

    const handleCoaUpload = async (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        setBusy(true);
        try {
            const { url } = await adminUploadImage(token, f);
            set("coa_image_url", url);
            toast.success("COA uploaded");
        } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
        setBusy(false);
    };

    const save = async () => {
        setBusy(true);
        try {
            // Only send changed keys to keep overrides minimal
            const patch = {};
            for (const k of Object.keys(draft)) {
                if (JSON.stringify(draft[k]) !== JSON.stringify(product.current[k])) {
                    patch[k] = draft[k];
                }
            }
            if (Object.keys(patch).length === 0) {
                toast.info("No changes");
                setBusy(false); return;
            }
            await adminPatchProductFull(token, product.id, patch);
            toast.success(`${draft.name} saved`);
            await onSaved();
            onClose();
        } catch { toast.error("Save failed"); }
        setBusy(false);
    };

    const reset = async () => {
        if (!confirm("Reset ALL overrides for this product? This removes every custom field.")) return;
        setBusy(true);
        try {
            await adminClearOverride(token, product.id);
            toast.success("Reset to defaults");
            await onSaved();
            onClose();
        } catch { toast.error("Reset failed"); }
        setBusy(false);
    };

    return (
        <div className="fixed inset-0 z-[999] bg-ink-base/90 backdrop-blur-sm flex items-start justify-center overflow-y-auto py-10 px-4" data-testid="product-editor">
            <div className="w-full max-w-3xl bg-ink-surface border border-ink-strong">
                <div className="flex items-center justify-between px-6 py-4 border-b border-ink">
                    <div>
                        <p className="overline text-neon text-[10px]">EDIT · {product.id}</p>
                        <h2 className="font-display text-xl font-bold text-ink mt-1">{draft.name}</h2>
                    </div>
                    <button onClick={onClose} className="p-2 text-ink-dim hover:text-ink" data-testid="editor-close"><X className="w-5 h-5" /></button>
                </div>

                <div className="p-6 space-y-6">
                    <div className="grid md:grid-cols-2 gap-4">
                        <Field label="Product name"><input className="inp" value={draft.name} onChange={(e) => set("name", e.target.value)} data-testid="edit-name" /></Field>
                        <Field label="Category"><input className="inp" value={draft.category} onChange={(e) => set("category", e.target.value)} /></Field>
                        <Field label="Variant / dosage label"><input className="inp" value={draft.dosage} onChange={(e) => set("dosage", e.target.value)} /></Field>
                        <Field label="COA URL"><input className="inp" value={draft.coa_url} onChange={(e) => set("coa_url", e.target.value)} /></Field>
                    </div>

                    <Field label="Short description (shown on cards)">
                        <textarea className="inp min-h-[70px]" value={draft.short} onChange={(e) => set("short", e.target.value)} data-testid="edit-short" />
                    </Field>

                    <div className="grid md:grid-cols-2 gap-4">
                        <Field label="What We Know">
                            <textarea className="inp min-h-[120px] text-xs" value={draft.what_we_know} onChange={(e) => set("what_we_know", e.target.value)} />
                        </Field>
                        <Field label="What It Does">
                            <textarea className="inp min-h-[120px] text-xs" value={draft.what_it_does} onChange={(e) => set("what_it_does", e.target.value)} />
                        </Field>
                    </div>

                    <div className="grid md:grid-cols-2 gap-4">
                        <Field label="Dosage info"><textarea className="inp min-h-[70px] text-xs" value={draft.dosage_info} onChange={(e) => set("dosage_info", e.target.value)} /></Field>
                        <Field label="How long a vial lasts"><textarea className="inp min-h-[70px] text-xs" value={draft.lasts} onChange={(e) => set("lasts", e.target.value)} /></Field>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <Field label="Price USD"><input type="number" step="0.01" className="inp font-mono" value={draft.price_usd} onChange={(e) => set("price_usd", parseFloat(e.target.value))} data-testid="edit-price" /></Field>
                        <Field label="Sale price USD"><input type="number" step="0.01" className="inp font-mono" value={draft.sale_price_usd ?? ""} placeholder="(none)" onChange={(e) => set("sale_price_usd", e.target.value === "" ? null : parseFloat(e.target.value))} /></Field>
                        <Toggle label="Sale active" checked={draft.sale_active} onChange={(v) => set("sale_active", v)} />
                        <Toggle label="In stock" checked={draft.in_stock} onChange={(v) => set("in_stock", v)} data-testid="edit-stock" />
                    </div>

                    <div className="grid grid-cols-2 gap-4 p-3 border border-gold/30 bg-gold/5" data-testid="edit-stock-count-block">
                        <Field label="Stock count (units in inventory · admin-only · auto-decrements on each paid order)">
                            <input type="number" step="1" min="0" className="inp font-mono"
                                value={draft.stock_count ?? ""} placeholder="(unlimited — leave blank)"
                                onChange={(e) => set("stock_count", e.target.value === "" ? null : parseInt(e.target.value, 10))}
                                data-testid="edit-stock-count" />
                        </Field>
                        <div className="text-[11px] text-gold leading-relaxed self-center font-mono">
                            {draft.stock_count == null ? (
                                <>UNLIMITED — set a number to enable auto-decrement &amp; auto-flip to SOLD OUT at 0.</>
                            ) : draft.stock_count === 0 ? (
                                <>● ZERO LEFT — buy button will be disabled site-wide.</>
                            ) : (
                                <>● {draft.stock_count} unit(s) left in inventory.</>
                            )}
                        </div>
                    </div>

                    <ProductMoleculeEditor
                        draft={draft}
                        set={set}
                        currentMolecule={product.current.molecule}
                        defaultMolecule={product.current.molecule_default}
                        onUseDefault={async () => {
                            try {
                                await adminClearMoleculeOverride(token, product.id);
                                toast.success("Molecule override cleared — using site default");
                                await onSaved();
                                onClose();
                            } catch { toast.error("Failed to clear override"); }
                        }}
                    />

                    <DoseCalcEditor draft={draft} set={set} />

                    <div className="grid grid-cols-2 gap-4">
                        <Toggle label="Show on homepage (featured)" checked={draft.featured} onChange={(v) => set("featured", v)} data-testid="edit-featured" />
                    </div>

                    <Field label="Front product photo · shown as the card hero (overrides the 3D molecule poster). Leave empty to keep the molecule.">
                        <div className="flex gap-3 items-start" data-testid="edit-front-image-block">
                            <div className="w-28 h-28 border border-gold/40 bg-ink-base shrink-0 overflow-hidden">
                                {draft.front_image ? <img src={draft.front_image} alt="" className="w-full h-full object-cover" /> : <div className="text-ink-mute text-xs p-2 text-center">no photo<br/>(molecule shown)</div>}
                            </div>
                            <div className="flex-1 space-y-2">
                                <input className="inp" placeholder="Paste real product photo URL (jpg/png)" value={draft.front_image || ""} onChange={(e) => set("front_image", e.target.value)} data-testid="edit-front-image-url" />
                                <div className="flex gap-2 flex-wrap">
                                    <input ref={frontImgRef} type="file" accept="image/*" onChange={handleFrontImageUpload} className="hidden" data-testid="edit-front-image-upload-input" />
                                    <button type="button" onClick={() => frontImgRef.current?.click()}
                                        className="inline-flex items-center gap-2 border border-gold/50 hover:border-gold text-gold px-3 py-2 text-xs font-mono uppercase" data-testid="edit-front-image-upload-btn">
                                        <Upload className="w-3.5 h-3.5" /> Upload product photo
                                    </button>
                                    {draft.front_image && (
                                        <button type="button" onClick={() => set("front_image", "")}
                                            className="inline-flex items-center gap-2 border border-red-500/40 hover:border-red-500 text-red-400 px-3 py-2 text-xs font-mono uppercase" data-testid="edit-front-image-clear">
                                            Clear · use molecule
                                        </button>
                                    )}
                                    <span className="text-[10px] text-ink-mute self-center">Replaces the molecule visual on Shop / Home cards · max 4MB</span>
                                </div>
                            </div>
                        </div>
                    </Field>

                    <Field label="Primary image">
                        <div className="flex gap-3 items-start">
                            <div className="w-28 h-28 border border-ink-strong bg-ink-base shrink-0 overflow-hidden">
                                {draft.image ? <img src={draft.image} alt="" className="w-full h-full object-cover" /> : <div className="text-ink-mute text-xs p-2">no image</div>}
                            </div>
                            <div className="flex-1 space-y-2">
                                <input className="inp" placeholder="Paste image URL" value={draft.image || ""} onChange={(e) => set("image", e.target.value)} data-testid="edit-image-url" />
                                <div className="flex gap-2">
                                    <input ref={fileRef} type="file" accept="image/*" onChange={handleUpload} className="hidden" data-testid="edit-image-upload" />
                                    <button type="button" onClick={() => fileRef.current?.click()}
                                        className="inline-flex items-center gap-2 border border-ink-strong hover:border-neon text-ink-dim hover:text-neon px-3 py-2 text-xs font-mono uppercase">
                                        <Upload className="w-3.5 h-3.5" /> Upload file
                                    </button>
                                    <span className="text-[10px] text-ink-mute self-center">URL or upload · max 4MB</span>
                                </div>
                            </div>
                        </div>
                    </Field>

                    <Field label="Extra gallery images (one URL per line)">
                        <textarea className="inp text-xs font-mono min-h-[70px]"
                            value={(draft.images || []).join("\n")}
                            onChange={(e) => set("images", e.target.value.split("\n").map((s) => s.trim()).filter(Boolean))} />
                    </Field>

                    <Field label="Certificate of Analysis (COA) image">
                        <div className="flex gap-3 items-start">
                            <div className="w-28 h-28 border border-ink-strong bg-white shrink-0 overflow-hidden flex items-center justify-center">
                                {draft.coa_image_url ? <img src={draft.coa_image_url} alt="" className="w-full h-full object-contain" /> : <div className="text-neutral-400 text-xs p-2 text-center">no COA</div>}
                            </div>
                            <div className="flex-1 space-y-2">
                                <input className="inp" placeholder="Paste Janoshik COA image URL" value={draft.coa_image_url || ""} onChange={(e) => set("coa_image_url", e.target.value)} data-testid="edit-coa-url" />
                                <div className="flex gap-2">
                                    <input ref={coaFileRef} type="file" accept="image/*" onChange={handleCoaUpload} className="hidden" data-testid="edit-coa-upload" />
                                    <button type="button" onClick={() => coaFileRef.current?.click()}
                                        className="inline-flex items-center gap-2 border border-ink-strong hover:border-neon text-ink-dim hover:text-neon px-3 py-2 text-xs font-mono uppercase">
                                        <Upload className="w-3.5 h-3.5" /> Upload COA
                                    </button>
                                    {draft.coa_image_url && (
                                        <button type="button" onClick={() => set("coa_image_url", "")}
                                            className="inline-flex items-center gap-2 border border-red-500/40 hover:border-red-500 text-red-400 px-3 py-2 text-xs font-mono uppercase">
                                            Clear
                                        </button>
                                    )}
                                    <span className="text-[10px] text-ink-mute self-center">Shows in the "View Certificate" lightbox on the product page</span>
                                </div>
                            </div>
                        </div>
                    </Field>
                </div>

                <ProductLabPhotosEditor
                    photos={draft.lab_photos || []}
                    onChange={(next) => set("lab_photos", next)}
                    token={token}
                />

                <div className="px-6 py-4 border-t border-ink flex items-center justify-between gap-3 bg-ink-base">
                    <button onClick={reset} disabled={busy} className="inline-flex items-center gap-2 border border-ink-strong text-ink-mute hover:text-red-400 px-3 py-2 text-xs font-mono uppercase disabled:opacity-40" data-testid="editor-reset">
                        <RotateCcw className="w-3.5 h-3.5" /> Reset all overrides
                    </button>
                    <div className="flex gap-2">
                        <button onClick={onClose} className="px-4 py-2 text-xs font-mono uppercase text-ink-dim hover:text-ink">CANCEL</button>
                        <button onClick={save} disabled={busy} className="inline-flex items-center gap-2 bg-neon hover:bg-neon-glow text-black px-6 py-2.5 font-bold text-xs tracking-wide uppercase disabled:opacity-40" data-testid="editor-save">
                            <Save className="w-3.5 h-3.5" /> {busy ? "SAVING…" : "SAVE CHANGES"}
                        </button>
                    </div>
                </div>
            </div>
            <style>{`.inp{width:100%;border:1px solid #1a2332;background:#0a0f1a;color:#e5ecf5;padding:10px 12px;font-size:13px;outline:none;transition:border .15s}.inp:focus{border-color:#10b981}`}</style>
        </div>
    );
}

/* ============ CREATE PRODUCT MODAL ============ */
function CreateProductModal({ token, onClose, onCreated }) {
    const [categories, setCategories] = useState(["GLP-1s", "Healing Peptides", "HGH & Ancillaries", "Nootropics", "Lab Supplies"]);
    React.useEffect(() => {
        // Pull live category list from /api/site/config so newly-added admin cats appear here too
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/site/config`)
            .then((r) => r.ok ? r.json() : null)
            .then((c) => {
                if (!c?.catalog_filters) return;
                const cats = c.catalog_filters.map((f) => f.cat).filter((x) => x && x !== "All");
                if (cats.length) setCategories(cats);
            }).catch(() => {});
    }, []);
    const [draft, setDraft] = useState({
        id: "",
        slug: "",
        name: "",
        category: "Healing Peptides",
        dosage: "",
        variant_label: "",
        variant_group: "",
        price_usd: 0,
        sale_price_usd: null,
        sale_active: false,
        in_stock: true,
        featured: false,
        short: "",
        what_we_know: "",
        what_it_does: "",
        dosage_info: "",
        lasts: "",
        image: "",
        coa_url: "https://janoshik.com/",
        coa_image_url: "",
        molecule_source: "",  // "" | "pubchem" | "pdb"
        molecule_id: "",
        molecule_label: "",
        is_new_arrival: false,
        // Dose calculator fields — admin can leave blank to auto-derive from dosage string
        dose_calc_vial_amount: "",
        dose_calc_unit: "",  // "mg" | "mcg" | "IU"
        dose_calc_default_dose: "",
        dose_calc_default_freq_days: "",
        dose_calc_kit_size: "",
    });
    const [busy, setBusy] = useState(false);
    const fileRef = useRef(null);
    const coaFileRef = useRef(null);

    const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

    // Auto-derive id/slug from name + dosage if user hasn't typed them yet
    const setName = (v) => {
        setDraft((d) => {
            const slug = (v + (d.dosage ? "-" + d.dosage : "")).toLowerCase()
                .replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
            return { ...d, name: v, id: d.id || slug, slug: d.slug || slug };
        });
    };

    const handleUpload = async (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        setBusy(true);
        try {
            const { url } = await adminUploadImage(token, f);
            set("image", url); toast.success("Image uploaded");
        } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
        setBusy(false);
    };
    const handleCoaUpload = async (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        setBusy(true);
        try {
            const { url } = await adminUploadImage(token, f);
            set("coa_image_url", url); toast.success("COA uploaded");
        } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
        setBusy(false);
    };

    const create = async () => {
        if (!draft.id || !draft.name || !draft.dosage) {
            toast.error("ID, name and dosage are required"); return;
        }
        if (!(draft.price_usd > 0)) {
            toast.error("Price must be greater than zero"); return;
        }
        const payload = {
            id: draft.id.trim(), slug: draft.slug.trim() || draft.id.trim(),
            name: draft.name, category: draft.category, dosage: draft.dosage,
            variant_label: draft.variant_label || draft.dosage,
            variant_group: draft.variant_group || null,
            price_usd: parseFloat(draft.price_usd),
            sale_price_usd: draft.sale_price_usd === null || draft.sale_price_usd === "" ? null : parseFloat(draft.sale_price_usd),
            sale_active: draft.sale_active,
            in_stock: draft.in_stock, featured: draft.featured,
            short: draft.short, what_we_know: draft.what_we_know, what_it_does: draft.what_it_does,
            dosage_info: draft.dosage_info, lasts: draft.lasts,
            image: draft.image, coa_url: draft.coa_url, coa_image_url: draft.coa_image_url,
        };
        if (draft.molecule_source && draft.molecule_id) {
            payload.molecule = {
                source: draft.molecule_source,
                id: draft.molecule_id.trim(),
                label: draft.molecule_label || null,
            };
        }
        // Optional dose calculator override (server auto-derives from dosage if blank)
        const dcVial = parseFloat(draft.dose_calc_vial_amount);
        const dcDose = parseFloat(draft.dose_calc_default_dose);
        if (dcVial > 0 && dcDose > 0 && draft.dose_calc_unit) {
            const freq = parseFloat(draft.dose_calc_default_freq_days) || 1;
            const dc = {
                vial_amount: dcVial,
                unit: draft.dose_calc_unit,
                default_dose: dcDose,
                default_freq_days: freq,
                freq_options: [
                    { label: "Daily", days: 1 },
                    { label: "Every other day", days: 2 },
                    { label: "2× per week", days: 3.5 },
                    { label: "Weekly", days: 7 },
                ],
            };
            const ks = parseInt(draft.dose_calc_kit_size);
            if (ks > 1) dc.kit_size = ks;
            payload.dose_calc = dc;
        }
        if (draft.is_new_arrival) payload.is_new_arrival = true;
        setBusy(true);
        try {
            await adminCreateProduct(token, payload);
            toast.success(`${draft.name} created`);
            await onCreated();
            onClose();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Create failed");
        }
        setBusy(false);
    };

    return (
        <div className="fixed inset-0 z-[999] bg-ink-base/90 backdrop-blur-sm flex items-start justify-center overflow-y-auto py-10 px-4" data-testid="create-product-modal">
            <div className="w-full max-w-3xl bg-ink-surface border border-ink-strong">
                <div className="flex items-center justify-between px-6 py-4 border-b border-ink">
                    <div>
                        <p className="overline text-neon text-[10px]">CREATE NEW PRODUCT</p>
                        <h2 className="font-display text-xl font-bold text-ink mt-1">New compound</h2>
                    </div>
                    <button onClick={onClose} className="p-2 text-ink-dim hover:text-ink" data-testid="create-close"><X className="w-5 h-5" /></button>
                </div>

                <div className="p-6 space-y-6">
                    <div className="grid md:grid-cols-2 gap-4">
                        <Field label="Product name *">
                            <input className="inp" value={draft.name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Sermorelin" data-testid="create-name" />
                        </Field>
                        <Field label="Category">
                            <select className="inp" value={draft.category} onChange={(e) => set("category", e.target.value)} data-testid="create-category">
                                {categories.map((c) => <option key={c} value={c}>{c}</option>)}
                            </select>
                        </Field>
                        <Field label="Dosage *"><input className="inp" placeholder="e.g. 5mg or 10,000 IU" value={draft.dosage} onChange={(e) => set("dosage", e.target.value)} data-testid="create-dosage" /></Field>
                        <Field label="Variant label (defaults to dosage)"><input className="inp" placeholder="(optional)" value={draft.variant_label} onChange={(e) => set("variant_label", e.target.value)} /></Field>
                        <Field label="Product ID (slug, no spaces) *"><input className="inp font-mono text-xs" value={draft.id} onChange={(e) => set("id", e.target.value.toLowerCase().replace(/[^a-z0-9-]+/g, "-"))} placeholder="auto-generated" data-testid="create-id" /></Field>
                        <Field label="URL slug"><input className="inp font-mono text-xs" value={draft.slug} onChange={(e) => set("slug", e.target.value.toLowerCase().replace(/[^a-z0-9-]+/g, "-"))} placeholder="auto-generated" /></Field>
                        <Field label="Variant group (links variants of same compound)"><input className="inp font-mono text-xs" placeholder="e.g. tirzepatide (optional)" value={draft.variant_group} onChange={(e) => set("variant_group", e.target.value.toLowerCase())} /></Field>
                        <Field label="COA URL"><input className="inp" value={draft.coa_url} onChange={(e) => set("coa_url", e.target.value)} /></Field>
                    </div>

                    <Field label="Short description (shown on cards)">
                        <textarea className="inp min-h-[70px]" value={draft.short} onChange={(e) => set("short", e.target.value)} placeholder="One-sentence summary for the catalog tile." data-testid="create-short" />
                    </Field>

                    <div className="grid md:grid-cols-2 gap-4">
                        <Field label="What We Know"><textarea className="inp min-h-[120px] text-xs" value={draft.what_we_know} onChange={(e) => set("what_we_know", e.target.value)} /></Field>
                        <Field label="What It Does"><textarea className="inp min-h-[120px] text-xs" value={draft.what_it_does} onChange={(e) => set("what_it_does", e.target.value)} /></Field>
                    </div>

                    <div className="grid md:grid-cols-2 gap-4">
                        <Field label="Dosage info"><textarea className="inp min-h-[70px] text-xs" value={draft.dosage_info} onChange={(e) => set("dosage_info", e.target.value)} /></Field>
                        <Field label="How long a vial lasts"><textarea className="inp min-h-[70px] text-xs" value={draft.lasts} onChange={(e) => set("lasts", e.target.value)} /></Field>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <Field label="Price USD *"><input type="number" step="0.01" className="inp font-mono" value={draft.price_usd} onChange={(e) => set("price_usd", parseFloat(e.target.value))} data-testid="create-price" /></Field>
                        <Field label="Sale price USD"><input type="number" step="0.01" className="inp font-mono" value={draft.sale_price_usd ?? ""} placeholder="(none)" onChange={(e) => set("sale_price_usd", e.target.value === "" ? null : parseFloat(e.target.value))} /></Field>
                        <Toggle label="Sale active" checked={draft.sale_active} onChange={(v) => set("sale_active", v)} />
                        <Toggle label="In stock" checked={draft.in_stock} onChange={(v) => set("in_stock", v)} />
                    </div>

                    <Toggle label="Show on homepage (featured)" checked={draft.featured} onChange={(v) => set("featured", v)} />

                    <Field label="Primary image">
                        <div className="flex gap-3 items-start">
                            <div className="w-28 h-28 border border-ink-strong bg-ink-base shrink-0 overflow-hidden">
                                {draft.image ? <img src={draft.image} alt="" className="w-full h-full object-cover" /> : <div className="text-ink-mute text-xs p-2">no image</div>}
                            </div>
                            <div className="flex-1 space-y-2">
                                <input className="inp" placeholder="Paste image URL" value={draft.image || ""} onChange={(e) => set("image", e.target.value)} />
                                <div className="flex gap-2">
                                    <input ref={fileRef} type="file" accept="image/*" onChange={handleUpload} className="hidden" />
                                    <button type="button" onClick={() => fileRef.current?.click()}
                                        className="inline-flex items-center gap-2 border border-ink-strong hover:border-neon text-ink-dim hover:text-neon px-3 py-2 text-xs font-mono uppercase">
                                        <Upload className="w-3.5 h-3.5" /> Upload file
                                    </button>
                                    <span className="text-[10px] text-ink-mute self-center">URL or upload · max 4MB</span>
                                </div>
                            </div>
                        </div>
                    </Field>

                    <Field label="Certificate of Analysis (COA) image">
                        <div className="flex gap-3 items-start">
                            <div className="w-28 h-28 border border-ink-strong bg-white shrink-0 overflow-hidden flex items-center justify-center">
                                {draft.coa_image_url ? <img src={draft.coa_image_url} alt="" className="w-full h-full object-contain" /> : <div className="text-neutral-400 text-xs p-2 text-center">no COA</div>}
                            </div>
                            <div className="flex-1 space-y-2">
                                <input className="inp" placeholder="Paste Janoshik COA image URL" value={draft.coa_image_url || ""} onChange={(e) => set("coa_image_url", e.target.value)} />
                                <div className="flex gap-2">
                                    <input ref={coaFileRef} type="file" accept="image/*" onChange={handleCoaUpload} className="hidden" />
                                    <button type="button" onClick={() => coaFileRef.current?.click()}
                                        className="inline-flex items-center gap-2 border border-ink-strong hover:border-neon text-ink-dim hover:text-neon px-3 py-2 text-xs font-mono uppercase">
                                        <Upload className="w-3.5 h-3.5" /> Upload COA
                                    </button>
                                </div>
                            </div>
                        </div>
                    </Field>

                    <div className="border border-ink p-5 bg-ink-base space-y-4" data-testid="create-dose-calc-block">
                        <div>
                            <p className="overline text-neon text-[10px]">"HOW LONG WILL IT LAST" CALCULATOR <span className="text-ink-mute">· optional</span></p>
                            <p className="text-xs text-ink-dim mt-1">Leave blank to auto-derive from the dosage string ("10mg" / "300 IU Kit" / "10 vials × 5mg"). Override here for exact protocol defaults.</p>
                        </div>
                        <div className="grid md:grid-cols-4 gap-3">
                            <Field label="Vial amount">
                                <input className="inp font-mono" placeholder="10" value={draft.dose_calc_vial_amount} onChange={(e) => set("dose_calc_vial_amount", e.target.value)} data-testid="create-dose-vial" />
                            </Field>
                            <Field label="Unit">
                                <select className="inp" value={draft.dose_calc_unit} onChange={(e) => set("dose_calc_unit", e.target.value)} data-testid="create-dose-unit">
                                    <option value="">(auto)</option>
                                    <option value="mg">mg</option>
                                    <option value="mcg">mcg</option>
                                    <option value="IU">IU</option>
                                </select>
                            </Field>
                            <Field label="Default dose">
                                <input className="inp font-mono" placeholder="2" value={draft.dose_calc_default_dose} onChange={(e) => set("dose_calc_default_dose", e.target.value)} data-testid="create-dose-default" />
                            </Field>
                            <Field label="Frequency (days)">
                                <input className="inp font-mono" placeholder="7" value={draft.dose_calc_default_freq_days} onChange={(e) => set("dose_calc_default_freq_days", e.target.value)} data-testid="create-dose-freq" />
                            </Field>
                            <Field label="Kit size (vials per kit · optional)">
                                <input className="inp font-mono" placeholder="10" value={draft.dose_calc_kit_size} onChange={(e) => set("dose_calc_kit_size", e.target.value)} data-testid="create-dose-kit-size" />
                            </Field>
                            <Field label="Mark as NEW ARRIVAL">
                                <label className="inline-flex items-center gap-2 text-sm text-ink-dim cursor-pointer mt-2">
                                    <input type="checkbox" checked={!!draft.is_new_arrival} onChange={(e) => set("is_new_arrival", e.target.checked)} data-testid="create-new-arrival" />
                                    Show in Home → New Arrivals section
                                </label>
                            </Field>
                        </div>
                    </div>

                    <div className="border border-ink p-5 bg-ink-base space-y-4">
                        <div>
                            <p className="overline text-neon text-[10px]">3D MOLECULE MODEL <span className="text-ink-mute">· optional</span></p>
                            <p className="text-xs text-ink-dim mt-1">Add a PubChem CID or PDB ID to render the interactive 3D structure on the product page. Leave blank if you only want the static image.</p>
                        </div>
                        <div className="grid md:grid-cols-3 gap-3">
                            <Field label="Source">
                                <select className="inp" value={draft.molecule_source} onChange={(e) => set("molecule_source", e.target.value)} data-testid="create-mol-source">
                                    <option value="">— none —</option>
                                    <option value="pubchem">PubChem (small peptide)</option>
                                    <option value="pdb">PDB (protein structure)</option>
                                </select>
                            </Field>
                            <Field label="ID">
                                <input className="inp font-mono" placeholder={draft.molecule_source === "pdb" ? "e.g. 1HGU" : "e.g. 9941957"}
                                    value={draft.molecule_id} onChange={(e) => set("molecule_id", e.target.value)} data-testid="create-mol-id" />
                            </Field>
                            <Field label="Label (optional)">
                                <input className="inp" placeholder="e.g. 15-aa pentadecapeptide" value={draft.molecule_label} onChange={(e) => set("molecule_label", e.target.value)} />
                            </Field>
                        </div>
                        <p className="text-[10px] text-ink-mute font-mono">
                            PUBCHEM CID → <a href="https://pubchem.ncbi.nlm.nih.gov/" target="_blank" rel="noopener" className="text-neon underline">pubchem.ncbi.nlm.nih.gov</a> · PDB ID → <a href="https://www.rcsb.org/" target="_blank" rel="noopener" className="text-neon underline">rcsb.org</a>
                        </p>
                    </div>
                </div>

                <div className="px-6 py-4 border-t border-ink flex items-center justify-end gap-3 bg-ink-base">
                    <button onClick={onClose} className="px-4 py-2 text-xs font-mono uppercase text-ink-dim hover:text-ink">CANCEL</button>
                    <button onClick={create} disabled={busy} className="inline-flex items-center gap-2 bg-neon hover:bg-neon-glow text-black px-6 py-2.5 font-bold text-xs tracking-wide uppercase disabled:opacity-40" data-testid="create-submit">
                        <Plus className="w-3.5 h-3.5" /> {busy ? "CREATING…" : "CREATE PRODUCT"}
                    </button>
                </div>
            </div>
            <style>{`.inp{width:100%;border:1px solid #1a2332;background:#0a0f1a;color:#e5ecf5;padding:10px 12px;font-size:13px;outline:none;transition:border .15s}.inp:focus{border-color:#10b981}`}</style>
        </div>
    );
}

function Field({ label, children }) {
    return (
        <label className="block">
            <span className="overline text-[10px] text-ink-mute mb-1.5 block">{label}</span>
            {children}
        </label>
    );
}

function Toggle({ label, checked, onChange, ...rest }) {
    return (
        <div>
            <span className="overline text-[10px] text-ink-mute mb-1.5 block">{label}</span>
            <button type="button" onClick={() => onChange(!checked)}
                className={`px-3 py-2 text-xs font-mono font-bold uppercase tracking-wider border w-full ${checked ? "border-neon text-neon bg-neon/5" : "border-ink-strong text-ink-dim bg-ink-base"}`}
                {...rest}>
                {checked ? <><Check className="w-3 h-3 inline" /> YES</> : "NO"}
            </button>
        </div>
    );
}

/* ============ ORDERS PANEL ============ */

/**
 * Status legend — the source of truth for what every payment status means and
 * how to render it. Used by the small status pill on each row, the legend block
 * at the top of the panel, and the journey timeline.
 */
const STATUS_META = {
    initiated: {
        label: "INITIATED · waiting for customer",
        color: "text-gold",
        bg: "bg-gold/10",
        border: "border-gold/40",
        explain: "Customer clicked Pay → backend created a NowPayments invoice. We are now waiting for them to complete payment on NowPayments' hosted crypto checkout. NO crypto has moved yet.",
        recommend: "Wait. If the order is older than 1 hour, the customer almost certainly abandoned — click 'Verify with NowPayments' to confirm and clear it from the queue.",
    },
    pending: {
        label: "PENDING",
        color: "text-gold",
        bg: "bg-gold/10",
        border: "border-gold/40",
        explain: "Payment is in flight or awaiting confirmation. For crypto this usually means the network is still confirming the transaction.",
        recommend: "Wait a few minutes. Use ‘Verify’ to force-check the provider.",
    },
    abandoned: {
        label: "ABANDONED · no payment",
        color: "text-red-400",
        bg: "bg-red-500/10",
        border: "border-red-500/40",
        explain: "Customer initiated checkout > 1 hour ago and never paid. There is NO crypto in your NowPayments account for this order. Safe to delete.",
        recommend: "Delete the row, OR click 'Verify with NowPayments' to confirm it's truly empty before deleting.",
    },
    paid: {
        label: "PAID · ready to ship",
        color: "text-neon",
        bg: "bg-neon/10",
        border: "border-neon/50",
        explain: "Payment captured. Crypto is in your NowPayments account. Customer received the confirmation email.",
        recommend: "Buy a discreet shipping label and send the customer their tracking number.",
    },
    unpaid: {
        label: "UNPAID",
        color: "text-red-400",
        bg: "bg-red-500/10",
        border: "border-red-500/40",
        explain: "NowPayments reports this invoice as failed. Common cause: crypto payment sent to wrong network or below the minimum.",
        recommend: "Delete or leave for records. No further action available.",
    },
    failed: {
        label: "FAILED",
        color: "text-red-400",
        bg: "bg-red-500/10",
        border: "border-red-500/40",
        explain: "Payment provider rejected the charge.",
        recommend: "If the customer reaches out, send them a fresh checkout link.",
    },
    refunded: {
        label: "REFUNDED",
        color: "text-ink-mute",
        bg: "bg-ink-surface",
        border: "border-ink-strong",
        explain: "Order was paid then refunded from the provider dashboard.",
        recommend: "If a refund was processed via NowPayments, this just reflects that.",
    },
    expired: {
        label: "EXPIRED",
        color: "text-ink-mute",
        bg: "bg-ink-surface",
        border: "border-ink-strong",
        explain: "NowPayments invoice expired without payment (default 20 minute window for BTC price lock).",
        recommend: "Safe to delete.",
    },
    canceled: {
        label: "CANCELED",
        color: "text-ink-mute",
        bg: "bg-ink-surface",
        border: "border-ink-strong",
        explain: "Customer or admin cancelled the session.",
        recommend: "Safe to delete.",
    },
};

function getStatusMeta(order) {
    // computed_status set by the backend ("abandoned" / "awaiting_customer") for initiated orders.
    if (order.computed_status === "abandoned" && order.payment_status === "initiated") {
        return STATUS_META.abandoned;
    }
    const key = (order.payment_status || "pending").toLowerCase();
    return STATUS_META[key] || {
        label: key.toUpperCase(),
        color: "text-ink-dim",
        bg: "bg-ink-surface",
        border: "border-ink-strong",
        explain: "Status not recognized by the dashboard. Click ‘Verify’ to force-refresh.",
        recommend: "Click Verify with provider.",
    };
}

/** Customer journey timeline — every recorded event with timestamp. */
function CustomerJourney({ order }) {
    const events = [];
    const ts = (s) => s ? new Date(s).toLocaleString() : null;
    if (order.created_at) {
        events.push({ when: order.created_at, label: "ORDER INITIATED", note: "Customer clicked 'Pay' — NowPayments invoice created. Awaiting crypto payment.", color: "text-gold" });
    }
    if (order.nowpayments_invoice_id || order.metadata?.nowpayments_invoice_id) {
        const iid = order.nowpayments_invoice_id || order.metadata?.nowpayments_invoice_id;
        events.push({ when: order.created_at, label: "NOWPAYMENTS INVOICE CREATED", note: `Invoice ${String(iid).slice(0, 24)}…`, color: "text-ink-dim" });
    }
    if (order.verified_at) {
        events.push({ when: order.verified_at, label: "ADMIN VERIFIED WITH NOWPAYMENTS", note: "Forced refresh against NowPayments API.", color: "text-ink-dim" });
    }
    if (order.payment_status === "expired") {
        events.push({ when: order.updated_at, label: "INVOICE EXPIRED", note: "Customer did not pay in the invoice window (~20 min crypto rate-lock).", color: "text-gold" });
    }
    if (order.payment_status === "partially_paid") {
        events.push({ when: order.updated_at, label: "PARTIALLY PAID", note: "Customer underpaid — accept, request diff, or refund.", color: "text-gold" });
    }
    if (order.payment_status === "failed") {
        events.push({ when: order.updated_at, label: "PAYMENT FAILED", note: "NowPayments reported the payment as failed.", color: "text-gold" });
    }
    if (order.payment_status === "paid") {
        events.push({ when: order.updated_at, label: order.manually_marked_paid ? "ADMIN MARKED PAID" : "PAYMENT CAPTURED", note: order.manually_marked_paid ? "Admin manually confirmed payment (out-of-band)." : "Funds confirmed by NowPayments. Money is in your account.", color: "text-neon" });
    }
    if (order.confirmation_email_sent) {
        events.push({ when: order.updated_at, label: "CONFIRMATION EMAIL SENT", note: "Order confirmation delivered to the customer.", color: "text-neon" });
    }
    if (order.abandoned_email_sent) {
        events.push({ when: order.abandoned_email_sent_at || order.updated_at, label: "RECOVERY EMAIL SENT", note: "'Finish your order' email delivered to customer.", color: "text-gold" });
    }
    if (order.label_url || order.tracking_number) {
        events.push({ when: order.tracking_sent_at || order.updated_at, label: order.label_url ? "SHIPPING LABEL PURCHASED" : "TRACKING ADDED", note: order.label_carrier ? `${order.label_carrier} · ${order.label_service || ""}` : (order.tracking_carrier || ""), color: "text-gold" });
    }
    if (order.tracking_sent_at) {
        events.push({ when: order.tracking_sent_at, label: "TRACKING EMAIL SENT TO CUSTOMER", note: order.tracking_number ? `Tracking #${order.tracking_number}` : "", color: "text-neon" });
    }
    if (order.status === "delivered") {
        events.push({ when: order.updated_at, label: "DELIVERED", note: "Carrier reported delivery via webhook.", color: "text-neon" });
    }
    if (events.length === 0) {
        return <p className="text-xs text-ink-mute">No recorded events yet.</p>;
    }
    return (
        <div className="space-y-3" data-testid="customer-journey">
            {events.map((e, i) => (
                <div key={i} className="flex items-start gap-3">
                    <div className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${e.color.replace("text-", "bg-")}`} />
                    <div className="flex-1 min-w-0">
                        <div className={`overline text-[10px] ${e.color} truncate`}>{e.label}</div>
                        <div className="text-[11px] font-mono text-ink-mute mt-0.5">{ts(e.when)}</div>
                        {e.note && <div className="text-xs text-ink-dim mt-0.5 leading-snug">{e.note}</div>}
                    </div>
                </div>
            ))}
        </div>
    );
}

function VerifyPaymentBtn({ token, orderId, onDone }) {
    const [busy, setBusy] = useState(false);
    const click = async () => {
        setBusy(true);
        try {
            const res = await adminVerifyPayment(token, orderId);
            if (res.ok === false) {
                toast.info(res.message || `Status unchanged: ${res.current_status || "unknown"}`, { duration: 8000 });
            } else if (res.provider === "nowpayments") {
                if (res.mapped_status === "partially_paid" && res.shortfall != null) {
                    toast.warning(
                        `⚠ Partially paid · customer paid ${res.actually_paid} ${res.pay_currency?.toUpperCase()} of ${res.pay_amount} owed (shortfall ${res.shortfall}). Decide: accept, request the difference, or refund.`,
                        { duration: 12000 }
                    );
                } else if (res.mapped_status === "paid") {
                    toast.success(`✓ PAID · NowPayments confirmed ${res.actually_paid || ""} ${res.pay_currency?.toUpperCase() || ""}`);
                } else if (res.mapped_status === "expired") {
                    toast.error(`Payment expired · customer never paid this invoice`);
                } else {
                    toast.info(`NowPayments: ${res.live_status} → ${res.mapped_status}`);
                }
            } else {
                toast.success("Verified");
            }
            onDone?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Verify failed");
        }
        setBusy(false);
    };
    return (
        <button onClick={click} disabled={busy}
            className="inline-flex items-center justify-center gap-2 border border-neon/50 hover:bg-neon/10 text-neon px-3 py-2 font-bold text-[10px] uppercase tracking-wider disabled:opacity-40"
            data-testid={`verify-payment-btn-${orderId}`}>
            <Search className="w-3 h-3" /> {busy ? "VERIFYING…" : "VERIFY WITH NOWPAYMENTS"}
        </button>
    );
}

function MarkPaidBtn({ token, orderId, onDone }) {
    const [busy, setBusy] = useState(false);
    const click = async () => {
        if (!window.confirm("Confirm you have verified this payment in your NowPayments dashboard before marking it paid.")) return;
        setBusy(true);
        try {
            await adminMarkPaid(token, orderId);
            toast.success("Order marked as PAID · stock decremented");
            onDone?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Mark paid failed");
        }
        setBusy(false);
    };
    return (
        <button onClick={click} disabled={busy}
            className="inline-flex items-center justify-center gap-2 bg-neon hover:bg-neon/90 text-black px-3 py-2 font-bold text-[10px] uppercase tracking-wider disabled:opacity-40"
            data-testid={`mark-paid-btn-${orderId}`}>
            <Check className="w-3 h-3" /> {busy ? "MARKING…" : "MARK PAID"}
        </button>
    );
}

function MarkDeliveredBtn({ token, orderId, onDone }) {
    const [busy, setBusy] = useState(false);
    const click = async () => {
        if (!window.confirm("Mark this order as delivered? Use when Canada Post confirms the tracked parcel has arrived.")) return;
        setBusy(true);
        try {
            await adminMarkDelivered(token, orderId);
            toast.success("Order marked as DELIVERED");
            onDone?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Mark delivered failed");
        }
        setBusy(false);
    };
    return (
        <button onClick={click} disabled={busy}
            className="inline-flex items-center justify-center gap-2 border border-gold/60 hover:bg-gold/10 text-gold px-3 py-2 font-bold text-[10px] uppercase tracking-wider disabled:opacity-40"
            data-testid={`mark-delivered-btn-${orderId}`}>
            <Truck className="w-3 h-3" /> {busy ? "MARKING…" : "MARK DELIVERED"}
        </button>
    );
}

function MarkCompletedBtn({ token, orderId, onDone }) {
    const [busy, setBusy] = useState(false);
    const click = async () => {
        if (!window.confirm("Mark this order as COMPLETED? This closes the order lifecycle (delivered + all follow-up done).")) return;
        setBusy(true);
        try {
            await adminMarkCompleted(token, orderId);
            toast.success("Order marked as COMPLETED");
            onDone?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Mark completed failed");
        }
        setBusy(false);
    };
    return (
        <button onClick={click} disabled={busy}
            className="inline-flex items-center justify-center gap-2 border border-neon/60 hover:bg-neon/10 text-neon px-3 py-2 font-bold text-[10px] uppercase tracking-wider disabled:opacity-40"
            data-testid={`mark-completed-btn-${orderId}`}>
            <Check className="w-3 h-3" /> {busy ? "MARKING…" : "MARK COMPLETED"}
        </button>
    );
}

/** Fires the 'Finish your order' recovery email to the customer.
 * Only shown for unpaid / expired / partially-paid orders. */
function SendRecoveryBtn({ token, order, onDone }) {
    const [busy, setBusy] = useState(false);
    const alreadySent = !!order.abandoned_email_sent;
    const click = async () => {
        const customerEmail = (order.shipping || {}).email || "";
        const msg = alreadySent
            ? `A recovery email was already sent to ${customerEmail} at ${order.abandoned_email_sent_at ? new Date(order.abandoned_email_sent_at).toLocaleString() : "unknown"}.\n\nSend it AGAIN?`
            : `Send 'Finish your order' recovery email to ${customerEmail}?\n\nThe customer will get a warm nudge with their cart items and a CONTINUE MY ORDER button back to checkout.`;
        if (!window.confirm(msg)) return;
        setBusy(true);
        try {
            const res = await adminSendRecoveryEmail(token, order.order_id, alreadySent);
            if (res?.result?.error) {
                toast.error(`Email attempt logged but Resend rejected it: ${res.result.error}`, { duration: 8000 });
            } else if (res?.result?.skipped) {
                toast.info(`Skipped: ${res.result.reason}`);
            } else {
                toast.success(`Recovery email queued to ${res.email}`);
            }
            onDone?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Recovery send failed");
        }
        setBusy(false);
    };
    return (
        <button onClick={click} disabled={busy}
            className={`inline-flex items-center justify-center gap-2 px-3 py-2 font-bold text-[10px] uppercase tracking-wider disabled:opacity-40 ${alreadySent ? "border border-gold/50 text-gold hover:bg-gold/10" : "bg-gold hover:bg-gold/90 text-black"}`}
            data-testid={`send-recovery-btn-${order.order_id}`}
            title={alreadySent ? "Recovery email already sent — click to resend" : "Send 'Finish your order' recovery email"}>
            <MailWarning className="w-3 h-3" />
            {busy ? "SENDING…" : (alreadySent ? "RESEND RECOVERY" : "SEND RECOVERY EMAIL")}
        </button>
    );
}

/** Deep diagnostics panel — pulls live NowPayments state + IPN raw + timeline.
 * Explains WHY an order didn't complete so the admin can decide what to do. */
function DiagnosticsPanel({ token, orderId }) {
    const [open, setOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const [data, setData] = useState(null);
    const [err, setErr] = useState(null);
    const load = async () => {
        setBusy(true); setErr(null);
        try {
            const d = await adminOrderDiagnostics(token, orderId);
            setData(d);
        } catch (e) {
            setErr(e?.response?.data?.detail || "Diagnostics failed");
        }
        setBusy(false);
    };
    const toggle = () => {
        const next = !open;
        setOpen(next);
        if (next && !data) load();
    };
    return (
        <div className="mt-4 border border-ink-strong" data-testid={`diagnostics-panel-${orderId}`}>
            <button onClick={toggle}
                className="w-full flex items-center justify-between px-3 py-2 bg-ink-surface hover:bg-ink-base text-xs font-mono uppercase tracking-wider text-ink-dim"
                data-testid={`diagnostics-toggle-${orderId}`}>
                <span className="inline-flex items-center gap-2"><Stethoscope className="w-3.5 h-3.5 text-neon" /> WHY DIDN'T THIS ORDER GO THROUGH?</span>
                <div className="inline-flex items-center gap-2">
                    {busy && <RefreshCw className="w-3 h-3 animate-spin text-neon" />}
                    <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
                </div>
            </button>
            {open && (
                <div className="p-4 space-y-4 bg-ink-base border-t border-ink-strong">
                    {err && <div className="text-xs text-red-400 font-mono">{err}</div>}
                    {!data && !err && <div className="text-xs text-ink-mute font-mono">Loading live NowPayments state…</div>}
                    {data && (
                        <>
                            {/* Diagnosis card */}
                            {data.diagnosis?.reason && (
                                <div className="p-3 border border-gold/40 bg-gold/5">
                                    <p className="overline text-gold text-[10px] mb-2">DIAGNOSIS</p>
                                    <p className="text-xs text-ink leading-relaxed">{data.diagnosis.reason}</p>
                                    {data.diagnosis.recommended_action && (
                                        <p className="mt-2 text-[11px] text-neon leading-relaxed"><strong className="uppercase font-mono tracking-wide">Do this:</strong> {data.diagnosis.recommended_action}</p>
                                    )}
                                </div>
                            )}

                            {/* Live NowPayments state */}
                            <div>
                                <p className="overline text-neon text-[10px] mb-2">LIVE NOWPAYMENTS STATE</p>
                                {data.live_error && <p className="text-[11px] text-red-400 font-mono mb-2">{data.live_error}</p>}
                                {data.live_status ? (
                                    <div className="p-3 bg-ink-surface border border-ink-strong text-[11px] font-mono text-ink-dim space-y-1">
                                        <div>source: <span className="text-neon">{data.live_status.source}</span></div>
                                        <div>payment_status: <span className="text-ink">{data.live_status.payment_status || data.live_status.invoice_status || "—"}</span></div>
                                        <div>pay_currency: <span className="text-ink">{(data.live_status.pay_currency || "").toUpperCase() || "—"}</span></div>
                                        <div>pay_amount: <span className="text-ink">{data.live_status.pay_amount || "—"}</span></div>
                                        <div>actually_paid: <span className="text-ink">{data.live_status.actually_paid || "0"}</span></div>
                                        <div>outcome_amount: <span className="text-ink">{data.live_status.outcome_amount || "—"}</span></div>
                                        {data.live_status.created_at && <div>NP created: <span className="text-ink">{data.live_status.created_at}</span></div>}
                                        {data.live_status.updated_at && <div>NP updated: <span className="text-ink">{data.live_status.updated_at}</span></div>}
                                    </div>
                                ) : (
                                    <p className="text-[11px] text-ink-mute">No live data (order has no NowPayments IDs, or provider unreachable).</p>
                                )}
                            </div>

                            {/* DB snapshot */}
                            <div>
                                <p className="overline text-neon text-[10px] mb-2">OUR DATABASE SAYS</p>
                                <div className="p-3 bg-ink-surface border border-ink-strong text-[11px] font-mono text-ink-dim space-y-1">
                                    {Object.entries(data.db_status || {}).map(([k, v]) => (
                                        <div key={k} className="flex gap-3">
                                            <span className="w-40 shrink-0 text-ink-mute">{k}</span>
                                            <span className="text-ink break-all">{v === null || v === undefined ? "—" : String(v)}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Timeline */}
                            {data.timeline?.length > 0 && (
                                <div>
                                    <p className="overline text-neon text-[10px] mb-2">EVENT TIMELINE</p>
                                    <div className="space-y-2">
                                        {data.timeline.map((e, i) => (
                                            <div key={i} className="flex items-start gap-3 text-xs">
                                                <div className="w-2 h-2 rounded-full mt-1.5 shrink-0 bg-neon" />
                                                <div className="flex-1 min-w-0">
                                                    <div className="overline text-neon text-[10px]">{e.label}</div>
                                                    <div className="text-[10px] font-mono text-ink-mute">{e.when ? new Date(e.when).toLocaleString() : "—"}</div>
                                                    {e.note && <div className="text-ink-dim mt-0.5">{e.note}</div>}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Last IPN payload */}
                            {data.last_ipn_raw && (
                                <div>
                                    <p className="overline text-neon text-[10px] mb-2">LAST IPN PAYLOAD (raw)</p>
                                    <pre className="p-3 bg-ink-surface border border-ink-strong text-[10px] font-mono text-ink-dim overflow-auto max-h-64 whitespace-pre-wrap break-all">{JSON.stringify(data.last_ipn_raw, null, 2)}</pre>
                                </div>
                            )}

                            <button onClick={load} disabled={busy}
                                className="inline-flex items-center gap-2 border border-ink-strong hover:border-neon text-ink-dim hover:text-neon text-[10px] font-mono uppercase tracking-wider px-3 py-1.5 disabled:opacity-40">
                                <RefreshCw className="w-3 h-3" /> REFRESH LIVE STATE
                            </button>
                        </>
                    )}
                </div>
            )}
        </div>
    );
}

function OrderDetail({ order, token, onTrackingSent, onDeleted }) {
    const [open, setOpen] = useState(false);
    const [trk, setTrk] = useState(order.tracking_number || "");
    const [carrier, setCarrier] = useState(order.tracking_carrier || "Canada Post Tracked");
    const [url, setUrl] = useState(order.tracking_url || "");
    const [busy, setBusy] = useState(false);
    const [labelBusy, setLabelBusy] = useState(false);
    const [labelInfo, setLabelInfo] = useState(order.label_url ? {
        label_url: order.label_url, tracking_number: order.tracking_number,
        tracking_url: order.tracking_url, carrier: order.label_carrier, service: order.label_service,
        rate_amount: order.label_amount_usd,
    } : null);
    const [labelSize, setLabelSize] = useState("4x6");

    const ship = order.shipping || {};
    const items = order.items || [];
    const isPaid = order.payment_status === "paid";
    const totalColor = isPaid ? "text-neon" : "text-gold";
    const statusMeta = getStatusMeta(order);

    const send = async () => {
        if (!trk) { toast.error("Enter tracking number"); return; }
        setBusy(true);
        try {
            await adminSendTracking(token, order.order_id, { tracking_number: trk, carrier, tracking_url: url || null });
            toast.success("Tracking email sent");
            onTrackingSent();
        } catch (e) { toast.error(e?.response?.data?.detail || "Send failed"); }
        setBusy(false);
    };

    const buyLabel = async () => {
        setLabelBusy(true);
        try {
            const res = await adminCreateLabel(token, order.order_id, { label_size: labelSize, description: "Research Supplies" });
            setLabelInfo(res);
            // Auto-prefill the tracking inputs so a single "Email tracking" click finishes the flow
            if (res.tracking_number) setTrk(res.tracking_number);
            if (res.carrier) setCarrier(res.carrier);
            if (res.tracking_url) setUrl(res.tracking_url);
            toast.success(`Label purchased · ${res.carrier} · $${(res.rate_amount || 0).toFixed(2)}`);
            onTrackingSent();
        } catch (e) {
            const msg = e?.response?.data?.detail || "Label purchase failed";
            toast.error(`${msg} · use the "Manual label" flow below to attach a Canada Post tracking # instead.`, { duration: 6000 });
        }
        setLabelBusy(false);
    };

    const attachManualLabel = async () => {
        if (!trk.trim()) { toast.error("Enter the tracking number from your Canada Post commercial portal first"); return; }
        setLabelBusy(true);
        try {
            const { data } = await api.post(`/admin/orders/${order.order_id}/manual-label`,
                { tracking_number: trk.trim(), carrier: carrier || "Canada Post", tracking_url: url || "" },
                { headers: { "X-Admin-Token": token } });
            if (data.tracking_url) setUrl(data.tracking_url);
            toast.success("Tracking attached · click 'Email tracking' to notify customer");
            onTrackingSent();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Manual label attach failed");
        }
        setLabelBusy(false);
    };

    const printLabel = () => {
        if (!labelInfo?.label_url) return;
        // Opens the PDF in a new tab → user clicks browser print → OS dialog handles
        // Bluetooth/USB thermal printers paired with the system. Most reliable cross-device path.
        const w = window.open(labelInfo.label_url, "_blank", "noopener");
        if (w) {
            try { setTimeout(() => { try { w.focus(); w.print(); } catch (_) { /* noop */ } }, 1200); } catch (_) { /* noop */ }
        }
    };

    const isShipped = order.status === "shipped" || !!order.tracking_number;

    const onDelete = async (e) => {
        e.stopPropagation();
        if (!window.confirm(`Delete order #${(order.order_id || "").slice(0, 8)}? This cannot be undone.`)) return;
        try {
            await adminDeleteOrder(token, order.order_id);
            toast.success("Order deleted");
            (onDeleted || onTrackingSent)();
        } catch (err) { toast.error(err?.response?.data?.detail || "Delete failed"); }
    };

    return (
        <div className="border-b border-ink" data-testid={`order-${order.order_id}`}>
            <button onClick={() => setOpen(!open)}
                className="w-full flex items-center justify-between p-4 hover:bg-ink-surface text-left transition-colors">
                <div className="flex items-center gap-4 min-w-0">
                    <div className={`w-2 h-2 rounded-full ${isShipped ? "bg-gold" : isPaid ? "bg-neon" : "bg-gold/60"}`} />
                    <div className="font-mono text-xs text-ink-dim shrink-0">#{(order.order_id || "").slice(0, 8)}</div>
                    <div className="text-sm text-ink truncate">{ship.full_name || ship.email || "—"}</div>
                    <div className="text-xs text-ink-mute truncate hidden sm:block">{ship.email}</div>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                    {isShipped && (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 font-mono text-[10px] font-bold uppercase tracking-wider border border-gold/60 bg-gold/10 text-gold" data-testid={`shipped-badge-${order.order_id}`}>
                            <Truck className="w-3 h-3" /> SHIPPED
                        </span>
                    )}
                    <span className={`inline-flex items-center gap-1 px-2.5 py-1 font-mono text-xs font-bold uppercase border ${isPaid ? "text-neon border-neon/50 bg-neon/10" : "text-gold border-gold/50 bg-gold/10"}`}>
                        {(order.currency || "usd").toUpperCase()} ${(order.amount || 0).toFixed(2)}
                    </span>
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 font-mono text-[10px] font-bold uppercase tracking-wider border ${statusMeta.color} ${statusMeta.border} ${statusMeta.bg}`} title={statusMeta.explain} data-testid={`order-status-pill-${order.order_id}`}>
                        {statusMeta.label}
                    </span>
                    <span className="text-xs text-ink-mute font-mono hidden md:inline">{(order.created_at || "").slice(0, 10)}</span>
                    <span
                        role="button"
                        tabIndex={0}
                        onClick={onDelete}
                        onKeyDown={(e) => { if (e.key === "Enter") onDelete(e); }}
                        className="text-ink-mute hover:text-red-400 transition-colors cursor-pointer"
                        title="Delete order"
                        data-testid={`delete-order-${order.order_id}`}
                    >
                        <Trash2 className="w-4 h-4" />
                    </span>
                    <Eye className="w-4 h-4 text-ink-mute" />
                </div>
            </button>

            {open && (
                <div className="bg-ink-base border-t border-ink">
                    {/* Status banner — explains what this status means + what to do next */}
                    <div className={`p-4 border-b border-ink ${statusMeta.bg}`} data-testid="order-status-banner">
                        <div className="flex items-start gap-3">
                            <Info className={`w-4 h-4 mt-0.5 shrink-0 ${statusMeta.color}`} />
                            <div className="flex-1 min-w-0">
                                <p className={`overline ${statusMeta.color} text-[10px]`}>{statusMeta.label}</p>
                                <p className="text-xs text-ink-dim mt-1 leading-relaxed">{statusMeta.explain}</p>
                                <p className={`text-[11px] mt-2 ${statusMeta.color} leading-relaxed`}>
                                    <strong className="font-mono uppercase tracking-wide">Recommended:</strong> {statusMeta.recommend}
                                </p>
                            </div>
                            <div className="flex flex-col gap-2">
                                <VerifyPaymentBtn token={token} orderId={order.order_id} onDone={onTrackingSent} />
                                {order.payment_status !== "paid" && (
                                    <MarkPaidBtn token={token} orderId={order.order_id} onDone={onTrackingSent} />
                                )}
                                {order.payment_status !== "paid" && (order.shipping || {}).email && (
                                    <SendRecoveryBtn token={token} order={order} onDone={onTrackingSent} />
                                )}
                                {order.status !== "delivered" && order.status !== "completed" && order.payment_status === "paid" && (
                                    <MarkDeliveredBtn token={token} orderId={order.order_id} onDone={onTrackingSent} />
                                )}
                                {order.status !== "completed" && order.payment_status === "paid" && (
                                    <MarkCompletedBtn token={token} orderId={order.order_id} onDone={onTrackingSent} />
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="p-6 grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                    <div>
                        <p className="overline text-neon mb-2">CUSTOMER</p>
                        <div className="text-sm text-ink space-y-1">
                            <div>{ship.full_name}</div>
                            <div className="text-ink-dim">{ship.email}</div>
                            <div className="text-ink-dim">{ship.phone || "—"}</div>
                        </div>
                        <p className="overline text-neon mt-5 mb-2">SHIPPING ADDRESS</p>
                        <div className="text-sm text-ink-dim leading-relaxed">
                            {ship.address_line1}<br />
                            {ship.address_line2 && <>{ship.address_line2}<br /></>}
                            {ship.city}, {ship.state} {ship.postal_code}<br />
                            {ship.country}
                        </div>
                        <p className="overline text-neon mt-5 mb-2 flex items-center gap-2"><Wallet className="w-3 h-3" /> PAYMENT DETAILS</p>
                        <div className="text-sm text-ink-dim space-y-1">
                            <div>Method: <span className="text-ink font-mono">{(order.metadata?.payment_method || "nowpayments_crypto").toUpperCase()}</span></div>
                            {order.nowpayments_invoice_id || order.metadata?.nowpayments_invoice_id ? (
                                <div className="truncate">NowPayments Invoice: <span className="font-mono text-[10px] text-ink-dim select-all">{order.nowpayments_invoice_id || order.metadata?.nowpayments_invoice_id}</span></div>
                            ) : order.session_id ? (
                                <div className="truncate">Session: <span className="font-mono text-[10px] text-ink-dim select-all">{order.session_id}</span></div>
                            ) : null}
                            {order.nowpayments_payment_id && (
                                <div className="truncate">NowPmts Payment ID: <span className="font-mono text-[10px] text-ink-dim select-all">{order.nowpayments_payment_id}</span></div>
                            )}
                            <div className="mt-1">Status: <span className={`font-mono text-xs ${statusMeta.color}`}>{order.payment_status || "pending"}</span></div>
                            {order.verified_at && (
                                <div className="text-[10px] text-ink-mute mt-1">Last verified: {new Date(order.verified_at).toLocaleString()}</div>
                            )}
                            {order.abandoned_email_sent && (
                                <div className="text-[10px] text-gold mt-1 font-mono">✓ Recovery email sent {order.abandoned_email_sent_at ? `· ${new Date(order.abandoned_email_sent_at).toLocaleString()}` : ""}</div>
                            )}
                            {(order.nowpayments_invoice_id || order.metadata?.nowpayments_invoice_id) && (
                                <a href={`https://nowpayments.io/payment/?iid=${encodeURIComponent(order.nowpayments_invoice_id || order.metadata?.nowpayments_invoice_id)}`} target="_blank" rel="noopener noreferrer"
                                    className="inline-flex items-center gap-1 text-[10px] font-mono uppercase text-neon hover:underline mt-2">
                                    Open in NowPayments <ExternalLink className="w-2.5 h-2.5" />
                                </a>
                            )}
                        </div>
                        <p className="overline text-neon mt-5 mb-2 flex items-center gap-2"><Clock className="w-3 h-3" /> CUSTOMER JOURNEY</p>
                        <CustomerJourney order={order} />
                        {order.payment_status !== "paid" && (
                            <DiagnosticsPanel token={token} orderId={order.order_id} />
                        )}
                    </div>

                    <div>
                        <p className="overline text-neon mb-2">ITEMS</p>
                        <div className="space-y-2">
                            {items.map((it, i) => (
                                <div key={i} className="flex justify-between text-sm border-b border-ink pb-2">
                                    <span className="text-ink font-mono text-xs">{it.product_id}</span>
                                    <span className="text-ink-dim font-mono">× {it.quantity}</span>
                                </div>
                            ))}
                        </div>
                        <div className="mt-4 pt-4 border-t border-ink-strong">
                            <div className={`inline-flex items-center px-4 py-3 bg-ink-surface border-2 ${isPaid ? "border-neon" : "border-gold/60"} w-full justify-between`} data-testid="order-total-highlight">
                                <span className="overline text-ink-mute">ORDER TOTAL</span>
                                <span className={`font-display text-2xl font-bold ${totalColor}`}>{(order.currency || "usd").toUpperCase()} ${(order.amount || 0).toFixed(2)}</span>
                            </div>
                            {order.metadata?.shipping_usd && parseFloat(order.metadata.shipping_usd) > 0 && (
                                <div className="mt-2 text-xs text-ink-mute font-mono">incl. shipping ${order.metadata.shipping_usd} USD</div>
                            )}
                        </div>
                    </div>

                    <div>
                        <p className="overline text-gold mb-2 flex items-center gap-2"><Printer className="w-3.5 h-3.5" /> SHIPPING LABEL</p>
                        {labelInfo ? (
                            <div className="mb-3 p-3 bg-neon/5 border border-neon/30 text-xs text-ink-dim space-y-1" data-testid="label-purchased-block">
                                <div className="overline text-neon text-[9px]">LABEL READY · {labelInfo.carrier} · {labelInfo.service}</div>
                                <div className="font-mono mt-1 text-ink truncate">{labelInfo.tracking_number}</div>
                                {labelInfo.rate_amount > 0 && <div className="text-ink-mute font-mono">${(labelInfo.rate_amount || 0).toFixed(2)} {labelInfo.rate_currency || "USD"}</div>}
                                <div className="flex gap-2 mt-2">
                                    <button onClick={printLabel}
                                        className="flex-1 inline-flex items-center justify-center gap-1.5 bg-neon hover:bg-neon-glow text-black px-3 py-2 font-bold text-[10px] uppercase tracking-wide"
                                        data-testid="print-label-btn">
                                        <Printer className="w-3 h-3" /> PRINT
                                    </button>
                                    <a href={labelInfo.label_url} download={`label-${order.order_id.slice(0, 8)}.pdf`} target="_blank" rel="noopener noreferrer"
                                        className="flex-1 inline-flex items-center justify-center gap-1.5 border border-ink-strong hover:border-neon text-ink-dim hover:text-neon px-3 py-2 font-bold text-[10px] uppercase tracking-wide"
                                        data-testid="download-label-btn">
                                        <Download className="w-3 h-3" /> PDF
                                    </a>
                                </div>
                                <p className="text-[10px] text-ink-mute leading-relaxed pt-2">Tip: Bluetooth thermal printers paired with your OS print via the standard system dialog. For unpaired Brother / Rollo / Zebra units, AirPrint or a USB-connected setup works without extra drivers.</p>
                            </div>
                        ) : (
                            <div className="mb-3 space-y-2" data-testid="label-purchase-block">
                                <select value={labelSize} onChange={(e) => setLabelSize(e.target.value)}
                                    className="w-full border border-ink-strong bg-ink-base text-ink px-3 py-2 text-xs font-mono focus:outline-none focus:border-neon"
                                    data-testid="label-size-select">
                                    <option value="4x6">4×6 thermal (recommended)</option>
                                    <option value="letter">Letter PDF (8.5×11)</option>
                                </select>
                                <button onClick={buyLabel} disabled={labelBusy || !ship.address_line1}
                                    className="w-full inline-flex items-center justify-center gap-2 bg-gold hover:bg-gold/90 text-black px-4 py-3 font-bold text-xs uppercase tracking-wide disabled:opacity-40"
                                    data-testid="buy-label-btn">
                                    <Printer className="w-3.5 h-3.5" /> {labelBusy ? "PURCHASING…" : "BUY DISCREET LABEL"}
                                </button>
                                <p className="text-[10px] text-ink-mute leading-relaxed">Description ships as <span className="text-ink-dim font-mono">"Research Supplies"</span>. One-click print after purchase. Cheapest carrier rate auto-selected.</p>
                                <div className="pt-3 mt-3 border-t border-ink">
                                    <p className="overline text-[9px] text-ink-mute mb-1.5">MANUAL LABEL FALLBACK</p>
                                    <p className="text-[10px] text-ink-mute leading-relaxed mb-2">Bought the label in Canada Post commercial portal instead? Enter the tracking # below in "SEND TRACKING" then click:</p>
                                    <button onClick={attachManualLabel} disabled={labelBusy || !trk.trim()}
                                        className="w-full inline-flex items-center justify-center gap-2 border border-gold text-gold hover:bg-gold/10 px-4 py-2 font-bold text-[11px] uppercase tracking-wide disabled:opacity-40"
                                        data-testid="attach-manual-label-btn">
                                        <Truck className="w-3.5 h-3.5" /> ATTACH MANUAL TRACKING #
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>

                    <div>
                        <p className="overline text-gold mb-2 flex items-center gap-2"><Truck className="w-3.5 h-3.5" /> SEND TRACKING</p>
                        {order.tracking_number && (
                            <div className="mb-4 p-3 bg-neon/5 border border-neon/30 text-xs text-ink-dim">
                                <div className="overline text-neon text-[9px]">CURRENT · sent {(order.tracking_sent_at || "").slice(0, 16).replace("T", " ")}</div>
                                <div className="font-mono mt-1 text-ink">{order.tracking_number}</div>
                                <div>{order.tracking_carrier}</div>
                            </div>
                        )}
                        <div className="space-y-3">
                            <input className="w-full border border-ink-strong bg-ink-base text-ink px-3 py-2 text-sm font-mono focus:outline-none focus:border-neon"
                                placeholder="Tracking number" value={trk} onChange={(e) => setTrk(e.target.value)} data-testid="tracking-number-input" />
                            <input className="w-full border border-ink-strong bg-ink-base text-ink px-3 py-2 text-sm focus:outline-none focus:border-neon"
                                placeholder="Carrier (e.g. Canada Post)" value={carrier} onChange={(e) => setCarrier(e.target.value)} />
                            <input className="w-full border border-ink-strong bg-ink-base text-ink px-3 py-2 text-sm focus:outline-none focus:border-neon"
                                placeholder="Tracking URL (optional)" value={url} onChange={(e) => setUrl(e.target.value)} />
                            <button onClick={send} disabled={busy || !ship.email}
                                className="w-full inline-flex items-center justify-center gap-2 bg-gold hover:bg-gold/90 text-black px-4 py-3 font-bold text-xs uppercase tracking-wide disabled:opacity-40"
                                data-testid="send-tracking-btn">
                                <Send className="w-3.5 h-3.5" /> {busy ? "SENDING…" : "EMAIL TRACKING TO CUSTOMER"}
                            </button>
                            {!ship.email && <p className="text-xs text-red-400">No email on file — cannot send.</p>}
                        </div>
                    </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function StatusLegendBlock() {
    const [open, setOpen] = useState(false);
    const keys = ["initiated", "abandoned", "pending", "paid", "unpaid", "failed", "refunded", "expired", "canceled"];
    return (
        <div className="border border-ink bg-ink-surface" data-testid="status-legend">
            <button
                type="button"
                onClick={() => setOpen(!open)}
                className="w-full flex items-center justify-between p-4 text-left hover:bg-ink-base/30"
            >
                <div className="flex items-center gap-3">
                    <Info className="w-4 h-4 text-neon" />
                    <span className="font-display text-sm font-bold text-ink">Order status legend</span>
                    <span className="text-[10px] text-ink-mute font-mono">what each status means + what to do</span>
                </div>
                <span className="font-mono text-[10px] text-ink-mute uppercase">{open ? "HIDE" : "SHOW"}</span>
            </button>
            {open && (
                <div className="p-4 border-t border-ink grid md:grid-cols-2 gap-3">
                    {keys.map((k) => {
                        const m = STATUS_META[k];
                        return (
                            <div key={k} className={`p-3 border ${m.border} ${m.bg}`}>
                                <span className={`inline-block font-mono text-[10px] font-bold uppercase tracking-wider ${m.color} mb-1`}>{m.label}</span>
                                <p className="text-[11px] text-ink-dim leading-relaxed">{m.explain}</p>
                                <p className={`text-[10px] mt-1.5 ${m.color} leading-snug`}>→ {m.recommend}</p>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

function OrdersPanel({ token, focusOrder }) {
    const [orders, setOrders] = useState([]);
    const load = () => adminListOrders(token).then((d) => setOrders(d.orders || [])).catch(() => {});
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);

    // Magic-link → scroll the focused order into view + flash highlight
    useEffect(() => {
        if (!focusOrder || orders.length === 0) return;
        setTimeout(() => {
            const el = document.querySelector(`[data-testid="order-${focusOrder}"]`);
            if (el) {
                el.scrollIntoView({ behavior: "smooth", block: "center" });
                el.style.transition = "box-shadow 0.4s";
                el.style.boxShadow = "0 0 0 2px rgba(16,185,129,0.6)";
                setTimeout(() => { el.style.boxShadow = ""; }, 3500);
            }
        }, 350);
    }, [focusOrder, orders]);

    const paidCount = orders.filter((o) => o.payment_status === "paid").length;
    const revenue = orders.filter((o) => o.payment_status === "paid").reduce((s, o) => s + (o.amount || 0), 0);

    const onClearAll = async () => {
        if (!window.confirm(`⚠ This will permanently delete ALL ${orders.length} orders. There is no undo. Continue?`)) return;
        if (!window.confirm("Are you absolutely sure? Type-confirmation required next.")) return;
        const t = window.prompt('Type DELETE-ALL to confirm:');
        if (t !== "DELETE-ALL") { toast.error("Confirmation text did not match — aborted."); return; }
        try {
            const r = await adminClearAllOrders(token);
            toast.success(`Deleted ${r.deleted} order${r.deleted === 1 ? "" : "s"}`);
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Clear failed"); }
    };

    return (
        <div className="space-y-6" data-testid="admin-orders">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-ink-strong">
                {[
                    { k: orders.length, v: "TOTAL ORDERS" },
                    { k: paidCount, v: "PAID ORDERS" },
                    { k: `$${revenue.toFixed(0)}`, v: "REVENUE" },
                    { k: orders.filter((o) => !o.tracking_number && o.payment_status === "paid").length, v: "AWAITING SHIP" },
                ].map((x) => (
                    <div key={x.v} className="bg-ink-surface p-5">
                        <div className="font-display text-3xl font-bold text-ink">{x.k}</div>
                        <div className="overline text-[10px] text-ink-mute mt-1">{x.v}</div>
                    </div>
                ))}
            </div>

            <StatusLegendBlock />

            {orders.length > 0 && (
                <div className="flex justify-end">
                    <button
                        type="button"
                        onClick={onClearAll}
                        className="inline-flex items-center gap-2 border border-red-500/40 text-red-300 hover:bg-red-500/10 hover:text-red-200 px-4 py-2 text-xs font-mono uppercase tracking-[0.15em] transition-colors"
                        data-testid="admin-clear-all-orders"
                    >
                        <Trash2 className="w-3.5 h-3.5" /> Clear all orders
                    </button>
                </div>
            )}

            {orders.length === 0 ? (
                <div className="text-ink-mute text-sm p-6 border border-ink bg-ink-surface">No orders yet.</div>
            ) : (
                <div className="border border-ink bg-ink-surface">
                    {orders.map((o) => <OrderDetail key={o.order_id || o.session_id} order={o} token={token} onTrackingSent={load} onDeleted={load} />)}
                </div>
            )}
        </div>
    );
}

/* ============ SYSTEM STATUS ============ */
function SystemStatusBlock({ token }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [smokeBusy, setSmokeBusy] = useState(false);
    const [smokeResult, setSmokeResult] = useState(null);

    const refresh = async () => {
        setLoading(true);
        try { setData(await adminSystemStatus(token)); }
        catch (_) { toast.error("Failed to load system status"); }
        setLoading(false);
    };
    useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [token]);

    const runSmokeTest = async () => {
        if (smokeBusy) return;
        setSmokeBusy(true);
        setSmokeResult(null);
        try {
            const res = await adminShippoSmokeTest(token);
            setSmokeResult(res);
            if (res.ok) toast.success("Shippo smoke test PASSED — label bought + tracking saved.");
            else toast.error(res.summary || "Shippo smoke test failed — see details.");
        } catch (e) {
            const detail = e?.response?.data?.detail || e?.message || "Smoke test request failed";
            setSmokeResult({ ok: false, summary: detail, steps: [] });
            toast.error(detail);
        }
        setSmokeBusy(false);
    };

    return (
        <div className="border border-ink bg-ink-surface" data-testid="system-status">
            <div className="flex items-center justify-between p-6 border-b border-ink">
                <div>
                    <h3 className="font-display text-lg font-bold text-ink flex items-center gap-2">
                        <Activity className="w-4 h-4 text-neon" /> System status
                    </h3>
                    <p className="text-xs text-ink-dim mt-1">Live health of every integration powering Helixora — and the next action you need to take if something is degraded.</p>
                </div>
                <button onClick={refresh} disabled={loading}
                    className="inline-flex items-center gap-2 border border-ink-strong hover:border-neon text-ink-dim hover:text-neon px-4 py-2 text-xs font-mono uppercase disabled:opacity-40"
                    data-testid="system-status-refresh">
                    <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> {loading ? "REFRESHING…" : "REFRESH"}
                </button>
            </div>

            {!data ? (
                <div className="p-6 text-ink-mute text-sm">Loading…</div>
            ) : (
                <>
                    <div className="divide-y divide-ink">
                        {Object.entries(data.integrations || {}).filter(([k]) => k !== "stripe").map(([k, v]) => (
                            <div key={k} className="p-5 flex items-start gap-4" data-testid={`status-row-${k}`}>
                                <div className={`w-3 h-3 rounded-full mt-1.5 shrink-0 ${v.ok ? "bg-neon" : "bg-gold animate-pulse"}`} />
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-baseline gap-3 flex-wrap">
                                        <span className="font-display text-base font-bold text-ink uppercase tracking-wide">{k}</span>
                                        <span className={`font-mono text-[10px] tracking-wider ${v.ok ? "text-neon" : "text-gold"}`}>
                                            {v.ok ? "● HEALTHY" : v.configured ? "● NEEDS SETUP" : "● NOT CONFIGURED"}
                                        </span>
                                    </div>
                                    <p className="text-xs text-ink-dim mt-1.5 leading-relaxed">{v.summary}</p>
                                    {(v.live_rates_count !== undefined) && (
                                        <p className="text-[10px] font-mono text-ink-mute mt-1">
                                            LIVE RATES: {v.live_rates_count} · CARRIERS: {v.carriers?.length ? v.carriers.join(", ") : "—"}
                                        </p>
                                    )}
                                    {v.action && (
                                        <div className="mt-3 p-3 border-l-2 border-gold bg-gold/5 text-xs text-gold leading-relaxed">
                                            <strong className="font-mono uppercase tracking-wider text-[10px] block mb-1">Action needed</strong>
                                            {v.action}
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>

                    {Array.isArray(data.recent_easyship_events) && data.recent_easyship_events.length > 0 && (
                        <div className="p-5 border-t border-ink bg-ink-base">
                            <p className="overline text-gold mb-3">RECENT EASYSHIP WEBHOOK EVENTS · last 5</p>
                            <div className="space-y-1.5 font-mono text-[11px]">
                                {data.recent_easyship_events.map((ev, i) => (
                                    <div key={i} className="flex gap-3 text-ink-dim">
                                        <span className="text-ink-mute w-32 truncate shrink-0">{(ev.received_at || "").slice(0, 19).replace("T", " ")}</span>
                                        <span className="text-neon w-32 truncate shrink-0">{ev.event_type || "—"}</span>
                                        <span className="text-gold truncate">{ev.status || "—"}</span>
                                        <span className="text-ink-mute truncate">{ev.tracking_number || ev.order_ref || ""}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    <div className="p-4 border-t border-ink bg-ink-base">
                        <p className="text-[10px] text-ink-mute font-mono">
                            Easyship webhook URL → <span className="text-ink-dim select-all">{(typeof window !== "undefined" ? window.location.origin : "")}/api/webhooks/easyship</span>
                        </p>
                        <p className="text-[10px] text-ink-mute font-mono mt-1">
                            Shippo webhook URL → <span className="text-ink-dim select-all">{(typeof window !== "undefined" ? window.location.origin : "")}/api/webhooks/shippo</span>
                        </p>
                    </div>

                    {/* SHIPPO END-TO-END SMOKE TEST */}
                    <div className="p-5 border-t border-ink bg-ink-base" data-testid="shippo-smoke-test-panel">
                        <div className="flex items-center justify-between gap-4 flex-wrap">
                            <div>
                                <p className="overline text-neon mb-1">SHIPPO · END-TO-END SMOKE TEST</p>
                                <p className="text-[11px] text-ink-dim leading-relaxed max-w-md">
                                    Creates a synthetic test order, hits Shippo for live rates, buys a real 4×6 Canada Post label (no charge on test tokens), and verifies tracking is persisted. Use this any time you rotate the Shippo token or change the warehouse address.
                                </p>
                            </div>
                            <button onClick={runSmokeTest} disabled={smokeBusy}
                                className="inline-flex items-center gap-2 bg-neon hover:bg-neon-glow text-black px-5 py-2.5 text-xs font-bold uppercase tracking-wider disabled:opacity-50 transition-colors"
                                data-testid="shippo-smoke-test-btn">
                                {smokeBusy ? "RUNNING…" : "▶ RUN SMOKE TEST"}
                            </button>
                        </div>

                        {smokeResult && (
                            <div className={`mt-5 border ${smokeResult.ok ? "border-neon/40 bg-neon/5" : "border-red-500/40 bg-red-500/5"}`} data-testid="shippo-smoke-test-result">
                                <div className="p-4 border-b border-ink-strong flex items-center justify-between gap-3 flex-wrap">
                                    <span className={`font-mono text-xs uppercase tracking-wider ${smokeResult.ok ? "text-neon" : "text-red-400"}`}>
                                        {smokeResult.ok ? "● PASS" : "● FAIL"} · {smokeResult.summary}
                                    </span>
                                    {smokeResult.tracking_number && (
                                        <span className="font-mono text-[11px] text-ink-dim">TRK · {smokeResult.tracking_number}</span>
                                    )}
                                </div>
                                <div className="divide-y divide-ink-strong">
                                    {(smokeResult.steps || []).map((s, i) => (
                                        <div key={i} className="p-4 flex items-start gap-3">
                                            <span className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${s.ok ? "bg-neon" : "bg-red-400"}`} />
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-baseline gap-3 flex-wrap">
                                                    <span className="font-mono text-[11px] uppercase tracking-wider text-ink">{s.step}</span>
                                                    <span className={`font-mono text-[10px] ${s.ok ? "text-neon" : "text-red-400"}`}>{s.ok ? "OK" : "FAIL"}</span>
                                                </div>
                                                <p className="text-[11px] text-ink-dim mt-1 leading-relaxed">{s.detail}</p>
                                                {s.step === "fetch_rates" && Array.isArray(s.data?.rates) && s.data.rates.length > 0 && (
                                                    <div className="mt-2 space-y-0.5 font-mono text-[10px]">
                                                        {s.data.rates.map((r, j) => (
                                                            <div key={j} className="text-ink-mute">
                                                                · {r.carrier} {r.service} · {r.amount_native} {r.currency} <span className="text-ink-dim">(≈ ${r.amount_usd} USD)</span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                                {s.step === "buy_label" && s.data?.label_url && (
                                                    <div className="mt-2 flex flex-wrap gap-2">
                                                        <a href={s.data.label_url} target="_blank" rel="noopener noreferrer"
                                                            className="inline-flex items-center gap-1.5 bg-gold hover:bg-gold/90 text-black px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider"
                                                            data-testid="smoke-test-label-link">
                                                            VIEW 4×6 LABEL PDF
                                                        </a>
                                                        {s.data?.tracking_url && (
                                                            <a href={s.data.tracking_url} target="_blank" rel="noopener noreferrer"
                                                                className="inline-flex items-center gap-1.5 border border-ink-strong hover:border-neon text-ink-dim hover:text-neon px-3 py-1.5 text-[10px] font-mono uppercase">
                                                                TRACK CARRIER →
                                                            </a>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </>
            )}
        </div>
    );
}


/* ============ SETTINGS PANEL ============ */
function HomeHeroEditor({ token, settings, reloadSettings }) {
    const seed = settings?.home_hero || {
        eyebrow: "/ 03 · PRIVACY PROTOCOL",
        title_line_1: "Plain boxes.",
        title_accent: "Zero",
        title_line_2: " branding.",
        body: "",
        stat_tiles: [{ k: "", v: "" }, { k: "", v: "" }, { k: "", v: "" }],
        image_url: "",
        image_caption_overline: "",
        image_caption: "",
    };
    const [draft, setDraft] = useState(seed);
    const [busy, setBusy] = useState(false);
    const [collapsed, setCollapsed] = useState(true);

    useEffect(() => {
        setDraft(settings?.home_hero || seed);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [settings?.home_hero]);

    const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
    const setTile = (i, field, val) => setDraft((d) => {
        const tiles = [...(d.stat_tiles || [])];
        tiles[i] = { ...(tiles[i] || {}), [field]: val };
        return { ...d, stat_tiles: tiles };
    });
    const addTile = () => setDraft((d) => ({ ...d, stat_tiles: [...(d.stat_tiles || []), { k: "", v: "" }] }));
    const removeTile = (i) => setDraft((d) => ({ ...d, stat_tiles: (d.stat_tiles || []).filter((_, j) => j !== i) }));

    const save = async () => {
        setBusy(true);
        try {
            await adminPatchSettings(token, { home_hero: draft });
            toast.success("Home hero section saved — refreshing site…");
            await reloadSettings();
        } catch { toast.error("Save failed"); }
        setBusy(false);
    };
    const resetToDefault = () => {
        if (!confirm("Reset the home hero section to the factory branded-vials copy?")) return;
        setDraft({
            eyebrow: "/ 03 · OUR BRANDING",
            title_line_1: "Lab-grade vials.",
            title_accent: "Premium",
            title_line_2: " craft.",
            body: "Every Helixora vial ships with premium matte-black labeling and uncompromising quality in both presentation and peptide purity. We take immense pride in every product we deliver.",
            stat_tiles: [
                { k: "48H", v: "PROCESSING WINDOW" },
                { k: "100%", v: "TRACKED PARCELS" },
                { k: "★", v: "SIGNATURE BRANDED VIALS" },
            ],
            image_url: "https://customer-assets.emergentagent.com/job_sterile-science/artifacts/o3hno6g2_image.png",
            image_caption_overline: "HELIXORA · BATCH 2026-Q2",
            image_caption: "Branded · lab-verified · ready to ship",
        });
    };

    return (
        <div className="border border-ink bg-ink-surface" data-testid="home-hero-editor">
            <div className="p-6 flex items-start justify-between gap-4 flex-wrap cursor-pointer" onClick={() => setCollapsed((c) => !c)}>
                <div>
                    <h3 className="font-display text-lg font-bold text-ink flex items-center gap-2">
                        <Edit3 className="w-4 h-4 text-neon" /> Home page · hero / privacy-protocol section
                    </h3>
                    <p className="text-xs text-ink-dim mt-1">Live-edit the big marketing block on the homepage. Replace it with branding copy whenever you're ready — the stat tiles, headline, body, and image are all customizable.</p>
                </div>
                <span className="font-mono text-[10px] text-ink-dim">{collapsed ? "▸ EXPAND" : "▾ COLLAPSE"}</span>
            </div>

            {!collapsed && (
                <div className="px-6 pb-6 space-y-4 border-t border-ink pt-5" data-testid="home-hero-editor-body">
                    <div className="grid md:grid-cols-2 gap-4">
                        <Field label="Eyebrow (section number / label)">
                            <input className="inp" value={draft.eyebrow || ""} onChange={(e) => set("eyebrow", e.target.value)} data-testid="home-hero-eyebrow" />
                        </Field>
                        <Field label="Image URL (background / hero visual)">
                            <input className="inp font-mono text-xs" value={draft.image_url || ""} onChange={(e) => set("image_url", e.target.value)} data-testid="home-hero-image-url" placeholder="https://…" />
                        </Field>
                    </div>

                    <div className="grid md:grid-cols-3 gap-4">
                        <Field label="Title · line 1"><input className="inp font-display" value={draft.title_line_1 || ""} onChange={(e) => set("title_line_1", e.target.value)} data-testid="home-hero-title-1" /></Field>
                        <Field label="Title · accent (gold word)"><input className="inp font-display text-gold" value={draft.title_accent || ""} onChange={(e) => set("title_accent", e.target.value)} data-testid="home-hero-title-accent" /></Field>
                        <Field label="Title · line 2 (after accent)"><input className="inp font-display" value={draft.title_line_2 || ""} onChange={(e) => set("title_line_2", e.target.value)} data-testid="home-hero-title-2" /></Field>
                    </div>

                    <Field label="Body paragraph">
                        <textarea className="inp min-h-[88px]" value={draft.body || ""} onChange={(e) => set("body", e.target.value)} data-testid="home-hero-body" />
                    </Field>

                    <div>
                        <div className="flex items-center justify-between mb-2">
                            <p className="overline text-neon text-[10px]">STAT TILES · headline number + label · up to 6</p>
                            <button type="button" onClick={addTile} disabled={(draft.stat_tiles || []).length >= 6}
                                className="text-[10px] font-mono uppercase tracking-wider border border-ink-strong hover:border-neon text-ink-dim hover:text-neon px-3 py-1.5 disabled:opacity-30" data-testid="home-hero-tile-add">
                                + ADD TILE
                            </button>
                        </div>
                        <div className="grid md:grid-cols-2 gap-3">
                            {(draft.stat_tiles || []).map((t, i) => (
                                <div key={i} className="border border-ink-strong p-3 bg-ink-base grid grid-cols-[1fr_2fr_auto] gap-2 items-end" data-testid={`home-hero-tile-${i}`}>
                                    <Field label={`Tile ${i + 1} · number`}>
                                        <input className="inp font-display text-neon" placeholder="48H" value={t.k || ""} onChange={(e) => setTile(i, "k", e.target.value)} />
                                    </Field>
                                    <Field label="Label">
                                        <input className="inp font-mono text-xs" placeholder="PROCESSING WINDOW" value={t.v || ""} onChange={(e) => setTile(i, "v", e.target.value)} />
                                    </Field>
                                    <button type="button" onClick={() => removeTile(i)} className="h-[38px] w-10 border border-ink-strong hover:border-red-400 hover:text-red-400 text-ink-dim" data-testid={`home-hero-tile-remove-${i}`}>
                                        <Trash2 className="w-3.5 h-3.5 mx-auto" />
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-4">
                        <Field label="Image caption · overline (small gold text)"><input className="inp font-mono text-xs" value={draft.image_caption_overline || ""} onChange={(e) => set("image_caption_overline", e.target.value)} data-testid="home-hero-cap-overline" /></Field>
                        <Field label="Image caption · title"><input className="inp" value={draft.image_caption || ""} onChange={(e) => set("image_caption", e.target.value)} data-testid="home-hero-caption" /></Field>
                    </div>

                    <div className="pt-4 border-t border-ink flex items-center justify-between gap-3 flex-wrap">
                        <button type="button" onClick={resetToDefault} className="text-[10px] font-mono uppercase tracking-wider text-ink-dim hover:text-gold" data-testid="home-hero-reset">
                            ↻ RESET TO FACTORY BRANDED-VIALS COPY
                        </button>
                        <button onClick={save} disabled={busy}
                            className="inline-flex items-center gap-2 bg-neon hover:bg-neon-glow text-black px-5 py-2.5 font-bold text-xs tracking-wide uppercase disabled:opacity-50"
                            data-testid="home-hero-save">
                            <Save className="w-3.5 h-3.5" /> {busy ? "SAVING…" : "SAVE HOME PAGE"}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}


/* ============ DISCOUNT CODES PANEL ============ */
function DiscountCodesPanel({ token }) {
    const [codes, setCodes] = useState(null);
    const [busy, setBusy] = useState(false);
    const [editing, setEditing] = useState(null);

    const blank = { code: "", type: "percent", value: 10, label: "", starts_at: "", expires_at: "", max_uses: "", min_subtotal_usd: "", enabled: true };

    const load = async () => {
        try { const d = await adminListDiscountCodes(token); setCodes(d.codes || []); }
        catch { toast.error("Failed to load codes"); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);

    const save = async () => {
        if (!editing) return;
        const trimmed = editing.code.trim().toUpperCase();
        if (!trimmed) { toast.error("Code is required"); return; }
        if (editing.type === "percent" && (editing.value < 0 || editing.value > 100)) { toast.error("Percent must be 0–100"); return; }
        setBusy(true);
        try {
            await adminUpsertDiscountCode(token, {
                code: trimmed, type: editing.type, value: parseFloat(editing.value),
                label: editing.label || null,
                starts_at: editing.starts_at ? new Date(editing.starts_at).toISOString() : null,
                expires_at: editing.expires_at ? new Date(editing.expires_at).toISOString() : null,
                max_uses: editing.max_uses === "" ? null : parseInt(editing.max_uses, 10),
                min_subtotal_usd: editing.min_subtotal_usd === "" ? null : parseFloat(editing.min_subtotal_usd),
                enabled: !!editing.enabled,
            });
            toast.success(`${trimmed} saved`);
            setEditing(null);
            await load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
        setBusy(false);
    };

    const del = async (code) => {
        if (!confirm(`Delete code ${code}?`)) return;
        try { await adminDeleteDiscountCode(token, code); toast.success(`${code} deleted`); await load(); }
        catch { toast.error("Delete failed"); }
    };

    const toggle = async (c) => {
        try {
            await adminUpsertDiscountCode(token, { ...c, enabled: !c.enabled });
            await load();
        } catch { toast.error("Toggle failed"); }
    };

    const fmtDate = (iso) => iso ? iso.slice(0, 10) : "—";
    const isExpired = (c) => c.expires_at && new Date(c.expires_at) < new Date();
    const isFuture = (c) => c.starts_at && new Date(c.starts_at) > new Date();

    return (
        <div className="border border-ink bg-ink-surface" data-testid="discount-codes-panel">
            <div className="p-6 flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <h3 className="font-display text-lg font-bold text-ink flex items-center gap-2">
                        <Star className="w-4 h-4 text-gold" /> Discount codes <span className="text-[10px] font-mono text-ink-mute">· timed · partnerships · checkout</span>
                    </h3>
                    <p className="text-xs text-ink-dim mt-1">Create promo codes that customers paste at checkout. Pick percent or fixed-amount, optionally schedule a start/end date, set a max-use cap, or require a minimum subtotal. Auto-validated server-side — never bypassable.</p>
                </div>
                <button onClick={() => setEditing(blank)} className="bg-neon hover:bg-neon-glow text-black px-4 py-2.5 text-xs font-bold uppercase tracking-wider" data-testid="discount-new-btn">
                    + NEW CODE
                </button>
            </div>

            {codes === null && <div className="px-6 pb-6 text-ink-mute text-sm">Loading…</div>}
            {codes && codes.length === 0 && <div className="px-6 pb-6 text-ink-mute text-sm" data-testid="discount-empty">No discount codes yet. Click + NEW CODE to create one.</div>}
            {codes && codes.length > 0 && (
                <table className="w-full text-sm border-t border-ink">
                    <thead className="bg-ink-base">
                        <tr className="text-left">
                            <th className="p-3 overline text-[9px] text-ink-mute font-mono">CODE</th>
                            <th className="p-3 overline text-[9px] text-ink-mute font-mono">VALUE</th>
                            <th className="p-3 overline text-[9px] text-ink-mute font-mono">SCHEDULE</th>
                            <th className="p-3 overline text-[9px] text-ink-mute font-mono">USES</th>
                            <th className="p-3 overline text-[9px] text-ink-mute font-mono">STATE</th>
                            <th className="p-3"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {codes.map((c) => (
                            <tr key={c.code} className="border-t border-ink hover:bg-ink-base/40" data-testid={`discount-row-${c.code}`}>
                                <td className="p-3">
                                    <div className="font-mono font-bold text-ink text-sm">{c.code}</div>
                                    {c.label && <div className="text-[10px] text-ink-mute">{c.label}</div>}
                                </td>
                                <td className="p-3 font-mono text-xs text-ink-dim">
                                    {c.type === "fixed" ? `$${c.value} OFF` : `${c.value}%`}
                                    {c.min_subtotal_usd ? <div className="text-[9px] text-ink-mute">min ${c.min_subtotal_usd}</div> : null}
                                </td>
                                <td className="p-3 font-mono text-[10px] text-ink-dim">
                                    {fmtDate(c.starts_at)} → {fmtDate(c.expires_at)}
                                </td>
                                <td className="p-3 font-mono text-xs text-ink-dim">
                                    {c.uses || 0}{c.max_uses ? ` / ${c.max_uses}` : ""}
                                </td>
                                <td className="p-3">
                                    <span className={`px-2 py-0.5 text-[9px] font-mono font-bold uppercase border ${!c.enabled ? "border-ink-strong text-ink-dim" : isExpired(c) ? "border-red-500/50 text-red-400 bg-red-950/30" : isFuture(c) ? "border-gold text-gold" : "border-neon text-neon bg-neon/5"}`}>
                                        {!c.enabled ? "DISABLED" : isExpired(c) ? "EXPIRED" : isFuture(c) ? "SCHEDULED" : "● LIVE"}
                                    </span>
                                </td>
                                <td className="p-3 text-right whitespace-nowrap">
                                    <button onClick={() => toggle(c)} className="text-[10px] font-mono uppercase tracking-wider text-ink-dim hover:text-gold mr-2" data-testid={`discount-toggle-${c.code}`}>
                                        {c.enabled ? "DISABLE" : "ENABLE"}
                                    </button>
                                    <button onClick={() => setEditing({
                                        ...c,
                                        starts_at: c.starts_at ? c.starts_at.slice(0, 10) : "",
                                        expires_at: c.expires_at ? c.expires_at.slice(0, 10) : "",
                                        max_uses: c.max_uses == null ? "" : c.max_uses,
                                        min_subtotal_usd: c.min_subtotal_usd == null ? "" : c.min_subtotal_usd,
                                    })} className="text-[10px] font-mono uppercase tracking-wider text-neon hover:text-neon-glow mr-2" data-testid={`discount-edit-${c.code}`}>
                                        EDIT
                                    </button>
                                    <button onClick={() => del(c.code)} className="text-[10px] font-mono uppercase tracking-wider text-ink-dim hover:text-red-400" data-testid={`discount-delete-${c.code}`}>
                                        DELETE
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}

            {editing && (
                <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={() => setEditing(null)} data-testid="discount-edit-modal">
                    <div className="bg-ink-base border border-neon/40 max-w-2xl w-full p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-start justify-between">
                            <h4 className="font-display text-xl font-bold text-ink">{codes?.find((x) => x.code === editing.code) ? "Edit code" : "New discount code"}</h4>
                            <button onClick={() => setEditing(null)} className="text-ink-dim hover:text-ink"><X className="w-4 h-4" /></button>
                        </div>
                        <div className="grid md:grid-cols-2 gap-3">
                            <Field label="Code (e.g. WELCOME15)">
                                <input className="inp font-mono uppercase" value={editing.code} onChange={(e) => setEditing({ ...editing, code: e.target.value.toUpperCase() })} data-testid="discount-code-input" />
                            </Field>
                            <Field label="Internal label (optional)">
                                <input className="inp" placeholder="Influencer X · Q2 partnership" value={editing.label || ""} onChange={(e) => setEditing({ ...editing, label: e.target.value })} data-testid="discount-label-input" />
                            </Field>
                            <Field label="Type">
                                <select className="inp" value={editing.type} onChange={(e) => setEditing({ ...editing, type: e.target.value })} data-testid="discount-type-input">
                                    <option value="percent">Percent (%)</option>
                                    <option value="fixed">Fixed amount (USD)</option>
                                </select>
                            </Field>
                            <Field label={editing.type === "fixed" ? "Amount off (USD)" : "Percent off"}>
                                <input className="inp font-mono" type="number" step="0.01" value={editing.value} onChange={(e) => setEditing({ ...editing, value: e.target.value })} data-testid="discount-value-input" />
                            </Field>
                            <Field label="Starts on (optional)">
                                <input className="inp font-mono" type="date" value={editing.starts_at || ""} onChange={(e) => setEditing({ ...editing, starts_at: e.target.value })} data-testid="discount-starts-input" />
                            </Field>
                            <Field label="Expires on (optional)">
                                <input className="inp font-mono" type="date" value={editing.expires_at || ""} onChange={(e) => setEditing({ ...editing, expires_at: e.target.value })} data-testid="discount-expires-input" />
                            </Field>
                            <Field label="Max uses (optional)">
                                <input className="inp font-mono" type="number" step="1" placeholder="(unlimited)" value={editing.max_uses} onChange={(e) => setEditing({ ...editing, max_uses: e.target.value })} data-testid="discount-max-uses-input" />
                            </Field>
                            <Field label="Minimum subtotal USD (optional)">
                                <input className="inp font-mono" type="number" step="0.01" placeholder="(no minimum)" value={editing.min_subtotal_usd} onChange={(e) => setEditing({ ...editing, min_subtotal_usd: e.target.value })} data-testid="discount-min-input" />
                            </Field>
                        </div>
                        <Toggle label="Enabled" checked={editing.enabled} onChange={(v) => setEditing({ ...editing, enabled: v })} />
                        <div className="flex justify-end gap-2 pt-3 border-t border-ink">
                            <button onClick={() => setEditing(null)} className="px-4 py-2 text-xs font-mono uppercase tracking-wider border border-ink-strong text-ink-dim hover:text-ink">CANCEL</button>
                            <button onClick={save} disabled={busy} className="bg-neon hover:bg-neon-glow text-black px-5 py-2 text-xs font-bold uppercase tracking-wider disabled:opacity-50" data-testid="discount-save-btn">
                                {busy ? "SAVING…" : "SAVE CODE"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}


/* ============ SETTINGS PANEL ============ */
function SettingsPanel({ token }) {
    const [s, setS] = useState(null);
    const [draft, setDraft] = useState({});
    const [busy, setBusy] = useState(false);
    const [testEmail, setTestEmail] = useState("");
    const [emailBusy, setEmailBusy] = useState(false);
    const load = () => adminGetSettings(token).then((d) => { setS(d.settings); setDraft({}); }).catch(() => {});
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);
    if (!s) return <div className="text-ink-mute">Loading…</div>;
    const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
    const save = async () => {
        if (Object.keys(draft).length === 0) { toast.info("No changes"); return; }
        setBusy(true);
        try { await adminPatchSettings(token, draft); toast.success("Settings saved"); await load(); }
        catch { toast.error("Save failed"); }
        setBusy(false);
    };
    const sendTest = async () => {
        if (!testEmail) { toast.error("Enter an email address"); return; }
        setEmailBusy(true);
        try {
            const res = await adminTestEmail(token, testEmail);
            if (res.result?.error) toast.error("Resend error: " + res.result.error);
            else toast.success(`Sent from ${res.from} → ${res.to}`);
        } catch (e) { toast.error(e?.response?.data?.detail || "Send failed"); }
        setEmailBusy(false);
    };
    const v = (k) => draft[k] !== undefined ? draft[k] : s[k];
    return (
        <div className="space-y-6" data-testid="admin-settings">
            <SystemStatusBlock token={token} />

            <HomeHeroEditor token={token} settings={s} reloadSettings={load} />

            <div className="border border-ink bg-ink-surface p-6" data-testid="email-operations-block">
                <h3 className="font-display text-lg font-bold text-ink flex items-center gap-2"><Mail className="w-4 h-4 text-neon" /> Email operations · orders@helixorapeptides.com</h3>

                {/* Inbound (replies) explainer */}
                <div className="mt-4 p-4 border border-gold/40 bg-gold/5" data-testid="inbound-explainer">
                    <p className="overline text-gold text-[10px] mb-2 flex items-center gap-2"><AlertCircle className="w-3 h-3" /> READING CUSTOMER REPLIES — read this first</p>
                    <p className="text-xs text-ink-dim leading-relaxed">
                        <strong className="text-ink">orders@helixorapeptides.com</strong> is the &laquo;FROM&raquo; address for every outbound order confirmation, tracking email, and admin alert.
                        <strong className="text-gold"> It is NOT a mailbox you can sign into</strong> &mdash; Resend (the email-sending provider) only handles
                        outbound delivery. To READ customer replies, you need to point that address to a real inbox you control. Set this up <em>once</em>, then
                        every reply lands in your Gmail/Outlook within seconds:
                    </p>
                    <ol className="mt-3 space-y-2 text-xs text-ink-dim list-decimal pl-5 leading-relaxed">
                        <li>
                            <strong className="text-ink">Cloudflare Email Routing (free, takes 5 min):</strong> Go to your Cloudflare dashboard &rarr;
                            <em>helixorapeptides.com</em> domain &rarr; <em>Email &rarr; Email Routing</em> &rarr; enable it &rarr; create a route:
                            <span className="font-mono text-neon"> orders@helixorapeptides.com → your-real-inbox@gmail.com</span>.
                            Cloudflare adds the MX records for you automatically.
                        </li>
                        <li>
                            <strong className="text-ink">Or use Google Workspace ($6/month):</strong> Add <em>helixorapeptides.com</em> as a domain in Google Workspace,
                            create the orders@ mailbox there. Pricier but you can also SEND from orders@ directly in Gmail.
                        </li>
                        <li>
                            <strong className="text-ink">Resend domain verification:</strong> Confirm that DKIM/SPF/Return-Path records are green for
                            <em> helixorapeptides.com</em> at <a href="https://resend.com/domains" target="_blank" rel="noopener noreferrer" className="text-neon hover:underline">resend.com/domains <ArrowUpRight className="w-2.5 h-2.5 inline -mt-0.5" /></a>.
                            Without this, outbound emails land in spam.
                        </li>
                    </ol>
                </div>

                <p className="text-xs text-ink-dim mt-5">Send a sample order-confirmation email through Resend to verify the sender display name and inbox delivery.</p>
                <div className="mt-3 flex gap-2 flex-wrap">
                    <input className="inp font-mono flex-1 min-w-[260px]" placeholder="recipient@example.com" value={testEmail} onChange={(e) => setTestEmail(e.target.value)} data-testid="settings-test-email-input" />
                    <button onClick={sendTest} disabled={emailBusy || !testEmail}
                        className="inline-flex items-center gap-2 bg-gold hover:bg-gold/90 text-black px-5 py-2.5 font-bold text-xs tracking-wide uppercase disabled:opacity-40"
                        data-testid="settings-send-test-email">
                        <Send className="w-3.5 h-3.5" /> {emailBusy ? "SENDING…" : "SEND TEST EMAIL"}
                    </button>
                </div>
                <p className="text-[10px] text-ink-mute mt-3 font-mono">FROM: Helixora Peptides &lt;orders@helixorapeptides.com&gt; · sent via Resend</p>
            </div>

            <div className="border border-ink bg-ink-surface p-6">
                <h3 className="font-display text-lg font-bold text-ink">Shippo shipping API</h3>
                <p className="text-xs text-ink-dim mt-1">Token used to fetch live carrier rates at checkout.</p>
                <div className="mt-4 grid md:grid-cols-2 gap-4">
                    <Field label={`Current token${s.shippo_token_set ? " · set" : " · not set"}`}>
                        <div className="font-mono text-xs text-ink-dim bg-ink-base border border-ink px-3 py-2">{s.shippo_token_preview || "(empty)"}</div>
                    </Field>
                    <Field label="New Shippo token (paste to replace)">
                        <input className="inp font-mono" placeholder="shippo_test_… or shippo_live_…"
                            value={draft.shippo_token || ""} onChange={(e) => set("shippo_token", e.target.value)} data-testid="settings-shippo-token" />
                    </Field>
                </div>
            </div>

            <div className="border border-ink bg-ink-surface p-6" data-testid="settings-nowpayments-ipn">
                <h3 className="font-display text-lg font-bold text-ink flex items-center gap-2">NowPayments IPN secret
                    <span className={`text-[10px] font-mono px-2 py-0.5 border ${s.nowpayments_ipn_secret_set ? "text-neon border-neon/50 bg-neon/10" : "text-gold border-gold/50 bg-gold/10"}`}>
                        {s.nowpayments_ipn_secret_set ? "ACTIVE" : "NOT SET"}
                    </span>
                </h3>
                <p className="text-xs text-ink-dim mt-1">
                    Verifies HMAC-SHA512 signatures on incoming crypto webhooks so spoofed payments can't mark orders paid. Configure your NowPayments dashboard webhook URL to:
                </p>
                <div className="mt-2 flex flex-wrap gap-2 text-[10px] font-mono">
                    <span className="text-neon select-all bg-ink-base border border-ink px-2 py-1">{(typeof window !== "undefined" ? window.location.origin : "")}/api/webhooks/nowpayments</span>
                </div>
                <div className="mt-4 grid md:grid-cols-2 gap-4">
                    <Field label={`Current secret${s.nowpayments_ipn_secret_set ? " · set" : " · not set"}`}>
                        <div className="font-mono text-xs text-ink-dim bg-ink-base border border-ink px-3 py-2">{s.nowpayments_ipn_secret_preview || "(empty)"}</div>
                    </Field>
                    <Field label="New IPN secret (paste from NowPayments → Settings → Store API)">
                        <input className="inp font-mono" placeholder="e.g. RyHj1r6817LRKIdNpvsvnX/6s0AZTbOy"
                            value={draft.nowpayments_ipn_secret || ""} onChange={(e) => set("nowpayments_ipn_secret", e.target.value)} data-testid="settings-nowpayments-ipn-input" />
                    </Field>
                </div>
            </div>

            <div className="border border-ink bg-ink-surface p-6">
                <h3 className="font-display text-lg font-bold text-ink">Warehouse (ship-from)</h3>
                <p className="text-xs text-ink-dim mt-1">Sent to Shippo as origin address.</p>
                <div className="mt-4 grid md:grid-cols-2 gap-4">
                    <Field label="Name"><input className="inp" value={v("warehouse_name")} onChange={(e) => set("warehouse_name", e.target.value)} /></Field>
                    <Field label="Phone"><input className="inp" value={v("warehouse_phone")} onChange={(e) => set("warehouse_phone", e.target.value)} /></Field>
                    <Field label="Street 1"><input className="inp" value={v("warehouse_street1")} onChange={(e) => set("warehouse_street1", e.target.value)} /></Field>
                    <Field label="Street 2"><input className="inp" value={v("warehouse_street2") || ""} onChange={(e) => set("warehouse_street2", e.target.value)} /></Field>
                    <Field label="City"><input className="inp" value={v("warehouse_city")} onChange={(e) => set("warehouse_city", e.target.value)} /></Field>
                    <Field label="State / Province"><input className="inp" value={v("warehouse_state")} onChange={(e) => set("warehouse_state", e.target.value)} /></Field>
                    <Field label="Postal code"><input className="inp" value={v("warehouse_zip")} onChange={(e) => set("warehouse_zip", e.target.value)} /></Field>
                    <Field label="Country (CA/US)"><input className="inp" value={v("warehouse_country")} onChange={(e) => set("warehouse_country", e.target.value.toUpperCase())} /></Field>
                </div>
            </div>

            <div className="border border-ink bg-ink-surface p-6">
                <h3 className="font-display text-lg font-bold text-ink">Default parcel</h3>
                <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4">
                    <Field label="Weight (lb)"><input type="number" step="0.1" className="inp font-mono" value={v("parcel_weight_lb")} onChange={(e) => set("parcel_weight_lb", parseFloat(e.target.value))} /></Field>
                    <Field label="Length (in)"><input type="number" step="0.1" className="inp font-mono" value={v("parcel_length_in")} onChange={(e) => set("parcel_length_in", parseFloat(e.target.value))} /></Field>
                    <Field label="Width (in)"><input type="number" step="0.1" className="inp font-mono" value={v("parcel_width_in")} onChange={(e) => set("parcel_width_in", parseFloat(e.target.value))} /></Field>
                    <Field label="Height (in)"><input type="number" step="0.1" className="inp font-mono" value={v("parcel_height_in")} onChange={(e) => set("parcel_height_in", parseFloat(e.target.value))} /></Field>
                </div>
            </div>

            <div className="border border-ink bg-ink-surface p-6" data-testid="settings-shipping-ranges">
                <h3 className="font-display text-lg font-bold text-ink">Shipping ranges</h3>
                <p className="text-xs text-ink-dim mt-1">Displayed on home page + product pages so researchers know what shipping costs before checkout.</p>
                <div className="mt-4 grid md:grid-cols-3 gap-4">
                    <Field label="🇨🇦🇺🇸 NA flat min (USD)">
                        <input type="number" step="1" className="inp font-mono" placeholder="30" value={v("na_shipping_fee_min_usd") ?? ""} onChange={(e) => set("na_shipping_fee_min_usd", parseFloat(e.target.value))} data-testid="settings-na-min" />
                    </Field>
                    <Field label="🇨🇦🇺🇸 NA flat max (USD)">
                        <input type="number" step="1" className="inp font-mono" placeholder="50" value={v("na_shipping_fee_max_usd") ?? ""} onChange={(e) => set("na_shipping_fee_max_usd", parseFloat(e.target.value))} data-testid="settings-na-max" />
                    </Field>
                    <Field label="NA ETA copy">
                        <input className="inp" placeholder="3–5 business days" value={v("na_shipping_eta") || ""} onChange={(e) => set("na_shipping_eta", e.target.value)} data-testid="settings-na-eta" />
                    </Field>
                </div>
            </div>

            <LabPhotosEditor draft={draft} v={v} set={set} />
            <CatalogFiltersEditor draft={draft} v={v} set={set} />

            <div className="border border-ink bg-ink-surface p-6" data-testid="settings-socials">
                <h3 className="font-display text-lg font-bold text-ink">Community / Social links</h3>
                <p className="text-xs text-ink-dim mt-1">Public URLs that show in the site footer + transactional emails. Leave a field empty to hide that channel.</p>
                <div className="mt-4 grid md:grid-cols-2 gap-4">
                    <Field label="Telegram"><input className="inp" placeholder="https://t.me/HelixoraSupportBot" value={v("social_telegram") || ""} onChange={(e) => set("social_telegram", e.target.value)} data-testid="settings-social-telegram" /></Field>
                    <Field label="X / Twitter"><input className="inp" placeholder="https://x.com/HPeptides" value={v("social_twitter") || ""} onChange={(e) => set("social_twitter", e.target.value)} data-testid="settings-social-twitter" /></Field>
                    <Field label="TikTok (future)"><input className="inp" placeholder="https://tiktok.com/@…" value={v("social_tiktok") || ""} onChange={(e) => set("social_tiktok", e.target.value)} data-testid="settings-social-tiktok" /></Field>
                    <Field label="Instagram (future)"><input className="inp" placeholder="https://instagram.com/…" value={v("social_instagram") || ""} onChange={(e) => set("social_instagram", e.target.value)} data-testid="settings-social-instagram" /></Field>
                </div>
            </div>

            <DiscountCodesPanel token={token} />

            <div className="flex justify-end">
                <button onClick={save} disabled={busy || Object.keys(draft).length === 0}
                    className="inline-flex items-center gap-2 bg-neon hover:bg-neon-glow text-black px-6 py-3 font-bold text-xs tracking-wide uppercase disabled:opacity-40" data-testid="settings-save">
                    <Save className="w-3.5 h-3.5" /> {busy ? "SAVING…" : "SAVE SETTINGS"}
                </button>
            </div>
            <style>{`.inp{width:100%;border:1px solid #1a2332;background:#0a0f1a;color:#e5ecf5;padding:10px 12px;font-size:13px;outline:none;transition:border .15s}.inp:focus{border-color:#10b981}`}</style>
        </div>
    );
}

/* ============ PRODUCTS PANEL ============ */
function ProductsPanel({ token }) {
    const [products, setProducts] = useState([]);
    const [editing, setEditing] = useState(null);
    const [creating, setCreating] = useState(false);
    const load = () => adminListProductsFull(token).then((d) => setProducts(d.products || [])).catch(() => {});
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);

    const byCategory = products.reduce((acc, p) => {
        (acc[p.current.category] = acc[p.current.category] || []).push(p);
        return acc;
    }, {});

    const quickToggle = async (p, field) => {
        try {
            await adminPatchProductFull(token, p.id, { [field]: !p.current[field] });
            toast.success(`${p.current.name} · ${field}: ${!p.current[field]}`);
            await load();
        } catch { toast.error("Update failed"); }
    };

    const handleDelete = async (p) => {
        const isCustom = p.is_custom;
        const msg = isCustom
            ? `Permanently delete custom product "${p.current.name} · ${p.variant_label}"?\n\nThis cannot be undone.`
            : `Delete "${p.current.name} · ${p.variant_label}"?\n\nThis hides it from the public catalog. You can restore it later from this admin panel.`;
        if (!confirm(msg)) return;
        try {
            await adminDeleteProduct(token, p.id);
            toast.success(isCustom ? "Custom product permanently deleted" : "Product deleted — hidden from catalog");
            await load();
        } catch { toast.error("Delete failed"); }
    };

    const handleRestore = async (p) => {
        try {
            await adminRestoreProduct(token, p.id);
            toast.success("Product restored — visible in catalog again");
            await load();
        } catch { toast.error("Restore failed"); }
    };

    return (
        <div className="space-y-10" data-testid="admin-products-panel">
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="p-4 border border-gold/30 bg-gold/5 text-xs text-ink-dim flex gap-3 flex-1 min-w-[280px]">
                    <AlertCircle className="w-4 h-4 text-gold shrink-0 mt-0.5" />
                    Click <strong className="text-ink">EDIT</strong> for full control (copy, images, sale, featured). Toggle featured / stock inline for quick changes.
                </div>
                <button onClick={() => setCreating(true)}
                    className="inline-flex items-center gap-2 bg-neon hover:bg-neon-glow text-black px-5 py-3 font-bold text-xs tracking-wide uppercase"
                    data-testid="admin-create-product-btn">
                    <Plus className="w-4 h-4" /> Create New Product
                </button>
            </div>
            {Object.entries(byCategory).map(([cat, items]) => (
                <div key={cat}>
                    <h2 className="overline text-neon mb-3">{cat} <span className="text-ink-mute">· {items.length} product{items.length !== 1 ? "s" : ""}</span></h2>
                    <div className="border border-ink bg-ink-surface overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="overline text-ink-mute border-b border-ink">
                                <tr>
                                    <th className="p-3 text-left">Product</th>
                                    <th className="p-3 text-left">Price USD</th>
                                    <th className="p-3 text-left">Stock</th>
                                    <th className="p-3 text-left">Sale</th>
                                    <th className="p-3 text-left">Featured</th>
                                    <th className="p-3 text-left">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.map((p) => {
                                    const c = p.current;
                                    const isDeleted = c.deleted;
                                    return (
                                        <tr key={p.id} className={`border-b border-ink hover:bg-ink-base ${isDeleted ? "opacity-50" : ""}`} data-testid={`admin-row-${p.id}`}>
                                            <td className="p-3">
                                                <div className="text-ink font-semibold">{c.name} <span className="text-ink-mute font-mono text-xs">· {p.variant_label}</span>{isDeleted && <span className="ml-2 text-red-400 overline text-[10px]">● DELETED</span>}</div>
                                                <div className="font-mono text-[10px] text-ink-mute">{p.id}{p.has_override && <span className="ml-2 text-gold">● OVERRIDE</span>}{p.is_custom && <span className="ml-2 text-neon">● CUSTOM</span>}</div>
                                            </td>
                                            <td className="p-3 font-mono text-ink">${c.price_usd.toFixed(2)}{c.sale_active && c.sale_price_usd && <div className="text-[10px] text-gold">SALE ${c.sale_price_usd}</div>}</td>
                                            <td className="p-3"><button onClick={() => quickToggle(p, "in_stock")} className={`px-2.5 py-1 text-[10px] font-mono font-bold uppercase border ${c.in_stock ? "border-neon text-neon bg-neon/5" : "border-red-500/50 text-red-400 bg-red-950/30"}`} data-testid={`admin-stock-${p.id}`}>{c.in_stock ? "IN STOCK" : "SOLD OUT"}</button></td>
                                            <td className="p-3"><button onClick={() => quickToggle(p, "sale_active")} className={`px-2.5 py-1 text-[10px] font-mono font-bold uppercase border ${c.sale_active ? "border-gold text-gold bg-gold/5" : "border-ink-strong text-ink-mute"}`}>{c.sale_active ? "ON SALE" : "OFF"}</button></td>
                                            <td className="p-3"><button onClick={() => quickToggle(p, "featured")} className={`px-2.5 py-1 text-[10px] font-mono font-bold uppercase border inline-flex items-center gap-1 ${c.featured ? "border-neon text-neon bg-neon/5" : "border-ink-strong text-ink-mute"}`} data-testid={`admin-featured-${p.id}`}><Star className="w-3 h-3" /> {c.featured ? "SHOWN" : "HIDDEN"}</button></td>
                                            <td className="p-3">
                                                <div className="flex items-center gap-2">
                                                    <button onClick={() => setEditing(p)} className="inline-flex items-center gap-1 bg-neon text-black px-3 py-1.5 text-[10px] font-bold uppercase" data-testid={`admin-edit-${p.id}`}>
                                                        <Edit3 className="w-3 h-3" /> Edit
                                                    </button>
                                                    {isDeleted ? (
                                                        <button onClick={() => handleRestore(p)} className="inline-flex items-center gap-1 border border-neon/50 text-neon px-2.5 py-1.5 text-[10px] font-bold uppercase hover:bg-neon/10" data-testid={`admin-restore-${p.id}`}>
                                                            <RotateCw className="w-3 h-3" /> Restore
                                                        </button>
                                                    ) : (
                                                        <button onClick={() => handleDelete(p)} className="inline-flex items-center gap-1 border border-red-500/50 text-red-400 px-2.5 py-1.5 text-[10px] font-bold uppercase hover:bg-red-950/30" data-testid={`admin-delete-${p.id}`}>
                                                            <Trash2 className="w-3 h-3" /> Delete
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            ))}
            {editing && <ProductEditorModal product={editing} token={token} onClose={() => setEditing(null)} onSaved={load} />}
            {creating && <CreateProductModal token={token} onClose={() => setCreating(false)} onCreated={load} />}
        </div>
    );
}

/* ============ SUPPLY PANEL — at-a-glance inventory editor ============ */
function SupplyPanel({ token }) {
    const [items, setItems] = useState(null);
    const [filter, setFilter] = useState("all"); // all / low / oos / unset
    const [drafts, setDrafts] = useState({}); // { product_id: stockCountDraft }
    const [savingId, setSavingId] = useState(null);
    const [search, setSearch] = useState("");

    const load = async () => {
        try {
            const data = await adminListProductsFull(token);
            setItems(data.products || []);
        } catch { toast.error("Failed to load supply"); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);

    const saveStock = async (p, value) => {
        const next = value === "" || value === null ? null : Math.max(0, parseInt(value, 10) || 0);
        setSavingId(p.id);
        try {
            await adminPatchProductFull(token, p.id, { stock_count: next });
            toast.success(`${p.current.name} → ${next == null ? "UNLIMITED" : next + " left"}`);
            await load();
            setDrafts((d) => { const n = { ...d }; delete n[p.id]; return n; });
        } catch { toast.error("Save failed"); }
        setSavingId(null);
    };

    const adjust = async (p, delta) => {
        const cur = p.current.stock_count;
        if (cur == null) { toast.info("Set a starting stock count first."); return; }
        await saveStock(p, Math.max(0, cur + delta));
    };

    if (!items) return <div className="text-ink-mute" data-testid="supply-loading">Loading inventory…</div>;

    const visible = items
        .filter((p) => !p.current.deleted)
        .filter((p) => {
            const c = p.current.stock_count;
            if (filter === "low") return typeof c === "number" && c > 0 && c <= 5;
            if (filter === "oos") return c === 0 || !p.current.in_stock;
            if (filter === "unset") return c == null;
            return true;
        })
        .filter((p) => {
            if (!search.trim()) return true;
            const q = search.trim().toLowerCase();
            return (p.current.name + " " + p.id + " " + (p.variant_label || "") + " " + p.current.category).toLowerCase().includes(q);
        });

    // Group by category for an at-a-glance lab-supply view
    const grouped = visible.reduce((acc, p) => {
        const cat = p.current.category || "Misc";
        (acc[cat] = acc[cat] || []).push(p);
        return acc;
    }, {});

    const totalUnits = items.reduce((s, p) => s + (typeof p.current.stock_count === "number" ? p.current.stock_count : 0), 0);
    const trackedCount = items.filter((p) => typeof p.current.stock_count === "number").length;
    const oosCount = items.filter((p) => p.current.stock_count === 0 || !p.current.in_stock).length;
    const lowCount = items.filter((p) => typeof p.current.stock_count === "number" && p.current.stock_count > 0 && p.current.stock_count <= 5).length;

    return (
        <div className="space-y-6" data-testid="admin-supply">
            {/* HERO STATS */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-ink-strong">
                {[
                    { k: totalUnits, v: "TOTAL UNITS · TRACKED", color: "text-neon" },
                    { k: trackedCount, v: "PRODUCTS · INVENTORIED", color: "text-ink" },
                    { k: lowCount, v: "LOW STOCK · ≤5 LEFT", color: lowCount > 0 ? "text-gold" : "text-ink-dim" },
                    { k: oosCount, v: "SOLD OUT", color: oosCount > 0 ? "text-red-400" : "text-ink-dim" },
                ].map((x) => (
                    <div key={x.v} className="bg-ink-surface p-5">
                        <div className={`font-display text-3xl font-bold ${x.color}`}>{x.k}</div>
                        <div className="overline text-[10px] text-ink-mute mt-1.5">{x.v}</div>
                    </div>
                ))}
            </div>

            {/* CONTROLS */}
            <div className="flex flex-wrap gap-3 items-center justify-between">
                <div className="flex flex-wrap gap-2" data-testid="supply-filters">
                    {[
                        { k: "all", label: "ALL" },
                        { k: "low", label: `LOW · ${lowCount}` },
                        { k: "oos", label: `SOLD OUT · ${oosCount}` },
                        { k: "unset", label: "NOT TRACKED" },
                    ].map((f) => (
                        <button key={f.k} onClick={() => setFilter(f.k)}
                            className={`px-3 py-1.5 text-[10px] font-mono uppercase tracking-wider border ${filter === f.k ? "border-neon text-neon bg-neon/10" : "border-ink-strong text-ink-dim hover:text-ink"}`}
                            data-testid={`supply-filter-${f.k}`}>
                            {f.label}
                        </button>
                    ))}
                </div>
                <input
                    className="border border-ink-strong bg-ink-base text-ink px-3 py-2 text-xs font-mono w-full sm:w-72 focus:outline-none focus:border-neon"
                    placeholder="Search by name / id / dosage…"
                    value={search} onChange={(e) => setSearch(e.target.value)}
                    data-testid="supply-search"
                />
            </div>

            {/* INVENTORY TABLE — grouped by category */}
            {Object.keys(grouped).length === 0 && (
                <p className="text-ink-mute text-sm" data-testid="supply-empty">No products match your filters.</p>
            )}
            {Object.entries(grouped).map(([cat, list]) => (
                <div key={cat} className="border border-ink bg-ink-surface" data-testid={`supply-category-${cat.replace(/\s+/g, '-').toLowerCase()}`}>
                    <div className="px-5 py-3 border-b border-ink flex items-center justify-between">
                        <h3 className="font-display text-base font-bold text-ink uppercase tracking-wide">{cat}</h3>
                        <span className="font-mono text-[10px] text-ink-mute">{list.length} SKU{list.length === 1 ? "" : "S"}</span>
                    </div>
                    <table className="w-full text-sm">
                        <thead className="bg-ink-base">
                            <tr className="text-left">
                                <th className="p-3 overline text-[9px] text-ink-mute font-mono">PRODUCT</th>
                                <th className="p-3 overline text-[9px] text-ink-mute font-mono">DOSAGE</th>
                                <th className="p-3 overline text-[9px] text-ink-mute font-mono">PRICE</th>
                                <th className="p-3 overline text-[9px] text-ink-mute font-mono">STOCK COUNT</th>
                                <th className="p-3 overline text-[9px] text-ink-mute font-mono">QUICK ADJUST</th>
                                <th className="p-3 overline text-[9px] text-ink-mute font-mono">STATE</th>
                            </tr>
                        </thead>
                        <tbody>
                            {list.map((p) => {
                                const c = p.current;
                                const draft = drafts[p.id];
                                const draftIsDirty = draft !== undefined && (draft === "" ? c.stock_count != null : parseInt(draft, 10) !== c.stock_count);
                                const isLow = typeof c.stock_count === "number" && c.stock_count > 0 && c.stock_count <= 5;
                                const isOOS = c.stock_count === 0 || !c.in_stock;
                                return (
                                    <tr key={p.id} className="border-t border-ink hover:bg-ink-base/40" data-testid={`supply-row-${p.id}`}>
                                        <td className="p-3">
                                            <div className="font-bold text-ink text-sm">{c.name}</div>
                                            <div className="text-[10px] font-mono text-ink-mute">{p.id}</div>
                                        </td>
                                        <td className="p-3 font-mono text-xs text-ink-dim">{p.variant_label || c.dosage}</td>
                                        <td className="p-3 font-mono text-xs text-ink">${c.price_usd.toFixed(2)}</td>
                                        <td className="p-3">
                                            <div className="flex items-center gap-2">
                                                <input type="number" step="1" min="0"
                                                    className={`w-20 inp font-mono text-sm ${draftIsDirty ? "border-gold" : ""}`}
                                                    placeholder="∞"
                                                    value={draft !== undefined ? draft : (c.stock_count == null ? "" : c.stock_count)}
                                                    onChange={(e) => setDrafts((d) => ({ ...d, [p.id]: e.target.value }))}
                                                    data-testid={`supply-input-${p.id}`}
                                                />
                                                {draftIsDirty && (
                                                    <button onClick={() => saveStock(p, draft)} disabled={savingId === p.id}
                                                        className="bg-neon hover:bg-neon-glow text-black px-3 py-1.5 text-[10px] font-bold uppercase disabled:opacity-50"
                                                        data-testid={`supply-save-${p.id}`}>
                                                        {savingId === p.id ? "…" : "SAVE"}
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                        <td className="p-3">
                                            {c.stock_count == null ? (
                                                <span className="font-mono text-[10px] text-ink-mute">— set initial count to enable</span>
                                            ) : (
                                                <div className="flex items-center gap-1">
                                                    <button onClick={() => adjust(p, -1)} disabled={savingId === p.id || c.stock_count <= 0}
                                                        className="w-8 h-8 border border-ink-strong hover:border-red-400 hover:text-red-400 text-ink-dim disabled:opacity-30 font-mono"
                                                        data-testid={`supply-minus-${p.id}`}>−1</button>
                                                    <button onClick={() => adjust(p, +1)} disabled={savingId === p.id}
                                                        className="w-8 h-8 border border-ink-strong hover:border-neon hover:text-neon text-ink-dim disabled:opacity-30 font-mono"
                                                        data-testid={`supply-plus-${p.id}`}>+1</button>
                                                    <button onClick={() => adjust(p, +10)} disabled={savingId === p.id}
                                                        className="w-12 h-8 border border-ink-strong hover:border-neon hover:text-neon text-ink-dim disabled:opacity-30 font-mono text-[10px]"
                                                        data-testid={`supply-plus10-${p.id}`}>+10</button>
                                                </div>
                                            )}
                                        </td>
                                        <td className="p-3">
                                            <span className={`px-2.5 py-1 text-[10px] font-mono font-bold uppercase border ${isOOS ? "border-red-500/50 text-red-400 bg-red-950/30" : isLow ? "border-gold text-gold bg-gold/10" : c.stock_count == null ? "border-ink-strong text-ink-dim" : "border-neon text-neon bg-neon/5"}`}>
                                                {isOOS ? "● SOLD OUT" : isLow ? `● LOW · ${c.stock_count}` : c.stock_count == null ? "UNLIMITED" : `● ${c.stock_count} LEFT`}
                                            </span>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            ))}

            <p className="text-[11px] text-ink-mute leading-relaxed">
                Stock count is admin-only — never shown on customer-facing pages. It auto-decrements by purchased quantity the moment payment hits PAID. When it reaches 0, the product flips to SOLD OUT site-wide automatically. Leave blank for products with unlimited supply.
            </p>
        </div>
    );
}

/* ============ MAIN ============ */
export default function Admin() {
    // Clean up legacy token from earlier "NovaPeptides" session that no longer matches the current server
    if (typeof window !== "undefined" && localStorage.getItem("nova_admin_token")) {
        localStorage.removeItem("nova_admin_token");
    }
    const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
    const [tab, setTab] = useState("products");
    const [focusOrder, setFocusOrder] = useState(null);

    // Magic-link consumer — if email button delivered ?magic=…&focus_order=…, redeem the magic
    // token for the real admin token, save it, then jump to Orders tab focused on that order.
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const magic = params.get("magic");
        const focus = params.get("focus_order");
        const err = params.get("magic_error");
        if (err) {
            toast.error(err === "expired" ? "Magic link expired — please sign in" : "Magic link invalid");
            window.history.replaceState({}, "", "/admin");
            return;
        }
        if (!magic) return;
        (async () => {
            try {
                const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/magic/redeem`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ token: magic }),
                });
                if (!r.ok) throw new Error("invalid");
                const data = await r.json();
                localStorage.setItem(TOKEN_KEY, data.admin_token);
                setToken(data.admin_token);
                setFocusOrder(focus || data.order_id);
                setTab("orders");
                toast.success(`Magic link OK — opening order #${(data.order_id || "").slice(0, 8)}`);
            } catch {
                toast.error("Magic link expired or invalid — please sign in normally");
            } finally {
                window.history.replaceState({}, "", "/admin");
            }
        })();
    }, []);

    // Verify the token by pinging an admin endpoint — if it 401s, clear and re-prompt login
    useEffect(() => {
        if (!token) return;
        adminListProductsFull(token).catch((err) => {
            if (err?.response?.status === 401) {
                localStorage.removeItem(TOKEN_KEY);
                setToken(null);
                toast.error("Session expired — please sign in again");
            }
        });
    }, [token]);

    if (!token) return <LoginForm onLogin={setToken} />;
    const logout = () => { localStorage.removeItem(TOKEN_KEY); localStorage.removeItem("nova_admin_token"); setToken(null); };

    return (
        <div className="min-h-screen bg-ink-base text-ink">
            <header className="border-b border-ink px-6 py-5 flex items-center justify-between">
                <div>
                    <Link to="/" className="overline text-ink-dim hover:text-neon">← HELIXORA.PEPTIDES</Link>
                    <h1 className="font-display text-2xl font-bold text-ink mt-1">Admin Console</h1>
                </div>
                <button onClick={logout} className="inline-flex items-center gap-1 overline text-ink-dim hover:text-neon" data-testid="admin-logout">
                    <LogOut className="w-4 h-4" /> LOGOUT
                </button>
            </header>

            <nav className="border-b border-ink px-6 flex gap-6" data-testid="admin-tabs">
                {[
                    { k: "products", icon: Package, label: "Products" },
                    { k: "supply", icon: Boxes, label: "Supply" },
                    { k: "orders", icon: ShoppingBag, label: "Orders" },
                    { k: "restricted", icon: ShieldAlert, label: "Restricted" },
                    { k: "settings", icon: SettingsIcon, label: "Settings" },
                ].map((t) => (
                    <button key={t.k} onClick={() => setTab(t.k)}
                        className={`py-3 text-xs font-mono uppercase tracking-[0.15em] border-b-2 inline-flex items-center gap-1.5 ${tab === t.k ? "border-neon text-neon" : "border-transparent text-ink-dim hover:text-ink"}`}
                        data-testid={`admin-tab-${t.k}`}>
                        <t.icon className="w-3.5 h-3.5" /> {t.label}
                    </button>
                ))}
            </nav>

            <main className="p-6 max-w-7xl">
                {tab === "products" && <ProductsPanel token={token} />}
                {tab === "supply" && <SupplyPanel token={token} />}
                {tab === "orders" && <OrdersPanel token={token} focusOrder={focusOrder} />}
                {tab === "restricted" && <RestrictedAdminPanel token={token} />}
                {tab === "settings" && <SettingsPanel token={token} />}
            </main>
        </div>
    );
}


function LabPhotosEditor({ draft, v, set }) {
    const photos = v("lab_photos") || [];
    const update = (i, key, val) => {
        const next = [...photos];
        next[i] = { ...(next[i] || {}), [key]: val };
        set("lab_photos", next);
    };
    const remove = (i) => set("lab_photos", photos.filter((_, idx) => idx !== i));
    const add = () => set("lab_photos", [...photos, { url: "", caption: "", label: "" }]);
    return (
        <div className="border border-ink bg-ink-surface p-6" data-testid="settings-lab-photos">
            <h3 className="font-display text-lg font-bold text-ink">Lab photos · "What you'll receive"</h3>
            <p className="text-xs text-ink-dim mt-1">Edit, re-order, or add lab/vial photos shown on every product page. Empty list = use built-in defaults.</p>
            <div className="mt-4 space-y-4">
                {photos.length === 0 && (
                    <div className="text-xs text-ink-mute italic px-3 py-4 border border-ink-strong border-dashed">
                        Currently using built-in default photos. Add an entry below to override.
                    </div>
                )}
                {photos.map((p, i) => (
                    <div key={i} className="border border-ink-strong p-4 bg-ink-base" data-testid={`lab-photo-row-${i}`}>
                        <div className="flex items-start gap-4">
                            {p.url ? (
                                <img src={p.url} alt="preview" className="w-24 h-24 object-cover border border-ink shrink-0" />
                            ) : (
                                <div className="w-24 h-24 border border-ink-strong border-dashed flex items-center justify-center text-[10px] text-ink-mute shrink-0">PREVIEW</div>
                            )}
                            <div className="flex-1 grid gap-2">
                                <Field label={`Image URL #${i + 1}`}>
                                    <input className="inp font-mono text-[11px]" placeholder="https://…" value={p.url || ""} onChange={(e) => update(i, "url", e.target.value)} data-testid={`lab-photo-url-${i}`} />
                                </Field>
                                <Field label="Label (overlay)">
                                    <input className="inp" placeholder="OUR LAB · HANGZHOU · HPLC + MS VERIFIED" value={p.label || ""} onChange={(e) => update(i, "label", e.target.value)} data-testid={`lab-photo-label-${i}`} />
                                </Field>
                                <Field label="Caption (lightbox)">
                                    <textarea rows="2" className="inp" placeholder="Describe what's in this photo …" value={p.caption || ""} onChange={(e) => update(i, "caption", e.target.value)} data-testid={`lab-photo-caption-${i}`} />
                                </Field>
                            </div>
                            <button type="button" onClick={() => remove(i)} className="text-ink-mute hover:text-red-400 shrink-0" data-testid={`lab-photo-remove-${i}`}>
                                <Trash2 className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                ))}
                <button type="button" onClick={add} className="inline-flex items-center gap-2 px-4 py-2 border border-ink-strong text-xs font-mono uppercase tracking-[0.15em] text-ink-dim hover:text-neon hover:border-neon transition-colors" data-testid="lab-photo-add">
                    + Add photo
                </button>
            </div>
        </div>
    );
}

function CatalogFiltersEditor({ draft, v, set }) {
    const [defaults, setDefaults] = React.useState([]);
    React.useEffect(() => {
        // Pre-populate the editor with the live merged list (defaults + admin extras) so the admin
        // can rename the existing categories instead of seeing an empty editor and accidentally
        // wiping them out.
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/site/config`)
            .then((r) => r.ok ? r.json() : null)
            .then((c) => {
                if (Array.isArray(c?.catalog_filters)) setDefaults(c.catalog_filters);
            })
            .catch(() => {});
    }, []);
    // If admin hasn't started editing (catalog_filters is empty/null in their draft), seed it with
    // the live list so every base category is right there to rename / reorder.
    const items = (v("catalog_filters") && v("catalog_filters").length > 0)
        ? v("catalog_filters")
        : defaults;
    const baseCats = new Set(["All", "GLP-1s", "Healing Peptides", "HGH & Ancillaries", "Nootropics", "Lab Supplies"]);
    const update = (i, key, val) => {
        const next = [...items];
        next[i] = { ...(next[i] || {}), [key]: val };
        set("catalog_filters", next);
    };
    const remove = (i) => {
        if (baseCats.has(items[i]?.cat)) {
            toast.error(`"${items[i].cat}" is a base category and cannot be deleted — only renamed.`);
            return;
        }
        set("catalog_filters", items.filter((_, idx) => idx !== i));
    };
    const move = (i, dir) => {
        const next = [...items];
        const j = i + dir;
        if (j < 0 || j >= next.length) return;
        [next[i], next[j]] = [next[j], next[i]];
        set("catalog_filters", next);
    };
    const add = () => set("catalog_filters", [...items, { cat: "", label: "", highlight: false }]);
    const resetToDefaults = () => {
        if (!window.confirm("Reset the Catalog dropdown to the default 7 categories? Any custom categories you added will remain.")) return;
        set("catalog_filters", defaults);
    };
    return (
        <div className="border border-ink bg-ink-surface p-6" data-testid="settings-catalog-filters">
            <div className="flex items-start justify-between gap-4">
                <div>
                    <h3 className="font-display text-lg font-bold text-ink">Catalog dropdown · header menu</h3>
                    <p className="text-xs text-ink-dim mt-1">Edit the items shown when hovering "CATALOG" in the header. <code className="text-neon">cat</code> = product category to filter; <code className="text-neon">label</code> = display text. Toggle highlight to render in gold (used for the EU section). Base categories are protected and cannot be deleted, only renamed.</p>
                </div>
                <button type="button" onClick={resetToDefaults} className="text-[10px] font-mono uppercase tracking-wider text-ink-mute hover:text-neon shrink-0" data-testid="catalog-filters-reset">↻ RESET TO DEFAULT</button>
            </div>
            <div className="mt-4 space-y-3">
                {items.map((it, i) => {
                    const isBase = baseCats.has(it.cat);
                    return (
                        <div key={i} className={`grid grid-cols-12 gap-2 items-center border ${isBase ? "border-ink-strong" : "border-gold/30"} p-3 bg-ink-base`} data-testid={`catalog-filter-row-${i}`}>
                            <div className="col-span-1 flex flex-col">
                                <button type="button" onClick={() => move(i, -1)} className="text-ink-mute hover:text-neon text-xs" data-testid={`catalog-filter-up-${i}`}>▲</button>
                                <button type="button" onClick={() => move(i, 1)} className="text-ink-mute hover:text-neon text-xs" data-testid={`catalog-filter-down-${i}`}>▼</button>
                            </div>
                            {isBase ? (
                                <div className="col-span-3 inp font-mono text-xs text-ink-mute bg-ink-surface flex items-center" title="Base category — cannot be renamed">{it.cat}</div>
                            ) : (
                                <input className="col-span-3 inp font-mono text-xs" placeholder="cat (e.g. GLP-1s)" value={it.cat || ""} onChange={(e) => update(i, "cat", e.target.value)} data-testid={`catalog-filter-cat-${i}`} />
                            )}
                            <input className="col-span-5 inp text-xs" placeholder="Display label" value={it.label || ""} onChange={(e) => update(i, "label", e.target.value)} data-testid={`catalog-filter-label-${i}`} />
                            <label className="col-span-2 flex items-center gap-2 text-[11px] font-mono text-ink-dim">
                                <input type="checkbox" checked={!!it.highlight} onChange={(e) => update(i, "highlight", e.target.checked)} data-testid={`catalog-filter-highlight-${i}`} />
                                Gold
                            </label>
                            {isBase ? (
                                <span className="col-span-1 text-[9px] font-mono text-ink-mute" title="Base category cannot be removed">🔒</span>
                            ) : (
                                <button type="button" onClick={() => remove(i)} className="col-span-1 text-ink-mute hover:text-red-400" data-testid={`catalog-filter-remove-${i}`}>
                                    <Trash2 className="w-4 h-4" />
                                </button>
                            )}
                        </div>
                    );
                })}
                <button type="button" onClick={add} className="inline-flex items-center gap-2 px-4 py-2 border border-ink-strong text-xs font-mono uppercase tracking-[0.15em] text-ink-dim hover:text-neon hover:border-neon transition-colors" data-testid="catalog-filter-add">
                    + Add custom category
                </button>
            </div>
        </div>
    );
}


/**
 * ProductLabPhotosEditor — embeds the per-product lab-photo override inside the product edit modal.
 * If the photos[] array is empty, the product inherits the site-wide defaults from Settings → Lab Photos.
 * Adding even one entry overrides the entire list for THIS product (so the admin can show different
 * lab/vial photos per product, since each peptide may come from a different lab or batch).
 */
function ProductLabPhotosEditor({ photos, onChange, token }) {
    const [busyIdx, setBusyIdx] = React.useState(-1);
    const update = (i, key, val) => {
        const next = [...photos];
        next[i] = { ...(next[i] || {}), [key]: val };
        onChange(next);
    };
    const remove = (i) => onChange(photos.filter((_, idx) => idx !== i));
    const move = (i, dir) => {
        const next = [...photos];
        const j = i + dir;
        if (j < 0 || j >= next.length) return;
        [next[i], next[j]] = [next[j], next[i]];
        onChange(next);
    };
    const add = () => onChange([...photos, { url: "", label: "", caption: "" }]);
    const inheritDefaults = () => onChange([]);
    const onUpload = async (i, e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        setBusyIdx(i);
        try {
            const { url } = await adminUploadImage(token, f);
            update(i, "url", url);
            toast.success("Photo uploaded");
        } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
        setBusyIdx(-1);
    };

    return (
        <div className="px-6 py-5 border-t border-ink bg-ink-surface" data-testid="product-lab-photos-editor">
            <div className="flex items-center justify-between mb-2">
                <div>
                    <h4 className="font-display text-base font-bold text-ink">Lab photos · "What you'll receive"</h4>
                    <p className="text-[11px] text-ink-dim mt-0.5">
                        {photos.length === 0
                            ? "Inheriting site-wide defaults. Add a photo below to override for THIS product only."
                            : `Overriding the site default with ${photos.length} custom photo${photos.length === 1 ? "" : "s"} for this product.`}
                    </p>
                </div>
                {photos.length > 0 && (
                    <button type="button" onClick={inheritDefaults} className="text-[10px] font-mono uppercase tracking-wider text-ink-mute hover:text-red-400" data-testid="lab-photos-inherit">
                        ↻ Inherit site default
                    </button>
                )}
            </div>

            <div className="space-y-3 mt-3">
                {photos.map((p, i) => (
                    <div key={i} className="border border-ink-strong p-3 bg-ink-base" data-testid={`product-lab-photo-${i}`}>
                        <div className="flex items-start gap-3">
                            <div className="flex flex-col gap-1 shrink-0">
                                <button type="button" onClick={() => move(i, -1)} className="text-ink-mute hover:text-neon text-xs px-1" data-testid={`product-lab-photo-up-${i}`}>▲</button>
                                <button type="button" onClick={() => move(i, 1)} className="text-ink-mute hover:text-neon text-xs px-1" data-testid={`product-lab-photo-down-${i}`}>▼</button>
                            </div>
                            {p.url ? (
                                <img src={p.url} alt="preview" className="w-20 h-20 object-cover border border-ink shrink-0" />
                            ) : (
                                <div className="w-20 h-20 border border-ink-strong border-dashed flex items-center justify-center text-[9px] text-ink-mute shrink-0">PREVIEW</div>
                            )}
                            <div className="flex-1 grid gap-2">
                                <div className="grid grid-cols-[1fr_auto] gap-2">
                                    <input
                                        className="inp font-mono text-[11px]"
                                        placeholder="Image URL or upload →"
                                        value={p.url || ""}
                                        onChange={(e) => update(i, "url", e.target.value)}
                                        data-testid={`product-lab-photo-url-${i}`}
                                    />
                                    <label className="inline-flex items-center gap-1.5 px-3 border border-ink-strong text-xs font-mono uppercase text-ink-dim hover:text-neon hover:border-neon cursor-pointer transition-colors">
                                        {busyIdx === i ? "↻ …" : "↑ Upload"}
                                        <input type="file" accept="image/*" onChange={(e) => onUpload(i, e)} className="hidden" data-testid={`product-lab-photo-upload-${i}`} />
                                    </label>
                                </div>
                                <input
                                    className="inp text-xs"
                                    placeholder="Label (overlay) — e.g. OUR LAB · HANGZHOU · HPLC + MS VERIFIED"
                                    value={p.label || ""}
                                    onChange={(e) => update(i, "label", e.target.value)}
                                    data-testid={`product-lab-photo-label-${i}`}
                                />
                                <textarea
                                    rows="2"
                                    className="inp text-xs"
                                    placeholder="Caption (lightbox) — describe what's pictured…"
                                    value={p.caption || ""}
                                    onChange={(e) => update(i, "caption", e.target.value)}
                                    data-testid={`product-lab-photo-caption-${i}`}
                                />
                            </div>
                            <button type="button" onClick={() => remove(i)} className="text-ink-mute hover:text-red-400 shrink-0" data-testid={`product-lab-photo-remove-${i}`}>
                                <Trash2 className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                ))}
                <button
                    type="button"
                    onClick={add}
                    className="inline-flex items-center gap-2 px-4 py-2 border border-ink-strong text-xs font-mono uppercase tracking-[0.15em] text-ink-dim hover:text-neon hover:border-neon transition-colors"
                    data-testid="product-lab-photo-add"
                >
                    + Add lab photo for this product
                </button>
            </div>
        </div>
    );
}


